/**
 * i18n de la interfaz.
 *
 * Ojo con la distincion, porque en esta app hay DOS traducciones distintas:
 *
 *   - Los textos de la interfaz (botones, etiquetas, mensajes) viven aqui.
 *   - Los textos de las CARTAS los traduce TCGdex y los sirve la API segun
 *     ?lang=. Nunca se traducen en el cliente.
 *
 * El idioma elegido se guarda en localStorage y viaja como parametro a la API,
 * asi que un recargado o un enlace compartido conservan el idioma.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const LANGS = [
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'en', label: 'English', short: 'EN' },
];

const STRINGS = {
  es: {
    appName: 'PokeTrack',
    tagline: 'Meta, mazos y precios de Pokémon TCG',
    search: 'Buscar cartas',
    searchPlaceholder: 'Nombre, ataque o efecto…',
    filters: 'Filtros',
    clearFilters: 'Limpiar filtros',
    type: 'Tipo',
    category: 'Categoría',
    rarity: 'Rareza',
    stage: 'Etapa',
    set: 'Expansión',
    format: 'Formato',
    regulationMark: 'Marca de regulación',
    any: 'Cualquiera',
    standard: 'Standard',
    expanded: 'Expanded',
    results: 'resultados',
    noResults: 'Ninguna carta coincide con esos filtros.',
    noResultsHint: 'Prueba quitando algún filtro o buscando por otro término.',
    loading: 'Cargando…',
    loadMore: 'Cargar más',
    hp: 'PS',
    retreat: 'Retirada',
    weakness: 'Debilidad',
    resistance: 'Resistencia',
    attacks: 'Ataques',
    abilities: 'Habilidades',
    illustrator: 'Ilustración',
    legalIn: 'Legal en',
    notLegal: 'Fuera de formato',
    close: 'Cerrar',
    fallbackNotice: 'Sin edición en español: se muestra el texto en inglés.',
    alternateName: 'En inglés',
    alternateNameEs: 'En español',
    prices: 'Precios',
    pricesLoading: 'Consultando precios…',
    pricesUnavailable: 'Precios no disponibles ahora mismo.',
    language: 'Idioma',
    dbEmpty: 'Estamos cargando el catálogo de cartas.',
    dbEmptyHint: 'Tarda unos minutos. Vuelve a intentarlo en un rato.',
    apiDown: 'No pudimos conectar con PokeTrack.',
    apiDownHint: 'Puede ser tu conexión o una caída momentánea del servidor. Reintenta en unos segundos.',
    retry: 'Reintentar',
    /* --- No se pudo cargar ---------------------------------------------------
       Un fallo de red NO es un vacio: el vacio habla del contenido ("no hay
       torneos en este periodo") y el fallo, de la conexion. Decir lo primero
       cuando pasa lo segundo hace que el visitante se vaya creyendo que el
       sitio no tiene datos. */
    loadFailTitle: 'No pudimos cargar esto',
    loadFailHint: 'Puede ser tu conexión o un problema nuestro. Tus filtros siguen puestos.',
    cards: 'cartas',
    zoomIn: 'Ampliar carta',
    zoomOut: 'Reducir carta',
    zoomHintIn: 'Pincha para ampliar · mantén pulsado y mueve para inclinar',
    zoomHintOut: 'Pincha para volver · mantén pulsado y mueve para inclinar',
    tiltHint: 'Mantén pulsado y mueve para inclinar',
    inChile: 'En Chile',
    from: 'Desde',
    fxNote: 'Cambio del Banco Central',
    outOfStock: 'Agotado',
    inStock: 'En stock',
    aggregateOffer: 'precio agregado',
    aggregateOfferHint: 'Mínimo entre los vendedores de un marketplace: no se sabe el idioma ni la condición de esa copia. Puede ser japonesa, reverse o de un vendedor con una sola unidad — se comprueba abriendo la publicación.',
    aggregateCheapest: 'sin idioma ni condición verificados',
    englishOnlyNote: 'Solo ediciones en inglés, que es lo que se juega en torneo.',
    noChilePrice: 'Sin stock en tiendas chilenas para esta carta.',
    skipToContent: 'Saltar al contenido',
    brandTagline: 'Datos para jugar mejor',
    buscadorTitulo: 'Buscar en todo',
    buscadorPlaceholder: 'Cartas, arquetipos, tus mazos…',
    buscadorCartas: 'Cartas',
    buscadorArquetipos: 'Arquetipos del meta',
    buscadorMisMazos: 'Mis mazos',
    buscadorEnMeta: 'Arquetipo del meta',
    buscadorMazoPropio: 'Mazo tuyo',
    buscadorRecientes: 'Lo último que abriste',
    buscadorAyuda: 'Escribe para buscar cartas, arquetipos del meta y tus mazos.',
    buscadorAtajoMover: 'moverse',
    buscadorAtajoAbrir: 'abrir',
    buscadorAtajoCerrar: 'cerrar',
    buscadorSinNada: 'Nada que se llame «{q}».',
    buscadorSinNadaPista: 'Prueba con menos letras, o con el nombre en inglés.',
    buscadorFallo: 'No pudimos buscar. Fue un problema al conectar, no lo que escribiste.',
    methodologyLink: 'Cómo se calcula',
    navCards: 'Cartas',
    navDecks: 'Mazos',
    myDecks: 'Mis mazos',
    newDeckAction: 'Nuevo mazo',
    buildByHand: 'Crear a mano',
    buildByHandHint: 'Busca cartas y arma la lista',
    buildByImport: 'Importar lista',
    buildByImportHint: 'Pega una lista de Pokémon TCG Live o Limitless',
    backToDecks: 'Mis mazos',
    draftDeck: 'Sin guardar',
    noDecksYet: 'Aún no tienes ningún mazo',
    noDecksHint: 'Pega una lista de Pokémon TCG Live, móntala carta a carta, o llévate una de las que están ganando torneos ahora mismo.',
    noDecksFromMeta: 'Ver el meta',
    /* SIN SESION la pantalla no puede decir "aún no tienes ningún mazo": no lo
       sabe. Quien entre desde otro dispositivo puede tener veinte guardados. */
    decksSignedOutTitle: 'Entra para ver tus mazos',
    decksSignedOutHint: 'Tus mazos viven en tu cuenta, así que los tienes en cualquier dispositivo. Si prefieres probar antes, monta uno ahora y guárdalo al entrar.',
    nCards: '{n} cartas',
    deckTitle: 'Importar lista',
    deckHint: 'Pega aquí la lista exportada desde Pokémon TCG Live.',
    importFailed: 'No pudimos leer la lista. No es tu texto: fue un problema al conectar. Vuelve a intentarlo.',
    copyFailed: 'El navegador no dejó copiar. Selecciona la lista y cópiala a mano.',
    deckPlaceholder: 'Pokémon: 12\n4 Spinarak ME3 1\n…',
    import: 'Importar',
    importing: 'Importando…',
    deckValid: 'Lista válida',
    deckInvalid: 'La lista tiene problemas',
    saveImported: 'Guardar mazo',
    savingDeck: 'Guardando…',
    deckNamePlaceholder: 'Nombre del mazo (opcional)',
    needsSessionDeck: 'Entra con tu cuenta para guardar el mazo.',
    importedDeckName: 'Mazo importado',
    deckSaved: 'Guardado como «{nombre}». Ya puedes asociarle partidas.',
    deckSaveFailed: 'No se pudo guardar el mazo.',
    saveOnlyResolved: 'Se guardarán {n} de {total} líneas: el resto no se pudo identificar.',
    copyList: 'Copiar lista',
    copied: 'Copiado',
    clearDeck: 'Vaciar',
    unresolvedLines: 'Líneas sin resolver',
    errDeckSize: 'La lista tiene {n} cartas y deben ser 60.',
    errCopyLimit: 'Hay {n} copias de {name}; el máximo son 4.',
    errNotLegal: '{name} no es legal en {format}.',
    errUnresolved: 'No se reconoce: {line}',
    didYouMean: '¿Querías decir…?',
    errNoPokemon: 'La lista no tiene ningún Pokémon.',
    errNoBasic: 'No hay ningún Pokémon Básico. Con esta lista no se puede empezar la partida.',
    errAceSpec: 'Hay {n} cartas ACE SPEC ({names}); solo se permite 1 por mazo.',
    warnAmbiguous: '{name} existe en varias expansiones; se eligió {chosen}.',
    totalCards: 'cartas en total',
    builderSearch: 'Busca cartas para añadir…',
    removeCopy: 'Quitar una copia',
    addCard: 'Añadir al mazo',
    yourDeck: 'Tu mazo',
    builderEmpty: 'Busca cartas arriba y pulsa una para añadirla.',
    exportDeck: 'Exportar al juego',
    vsMetaTitle: 'Tu mazo contra el meta',
    vsMetaLists: 'sobre {n} listas del meta',
    vsMetaCore: 'del núcleo',
    vsMetaShare: 'Presencia',
    vsMetaWr: 'Victorias',
    vsMetaMissing: 'Del núcleo, esto no lo llevas',
    vsMetaUnusual: 'Esto casi nadie lo juega',
    vsMetaOthers: 'También se parece a',
    vsMetaHint:
      'El parecido se mide solo contra el núcleo del arquetipo: las cartas que lleva al menos el 60% de las listas. Contar todas daría un porcentaje alto siempre, porque en Standard cualquier par de mazos comparte Trainers y energías básicas. Llevar algo inusual no es un error; a veces es justo la razón por la que un mazo gana.',
    analistaPedir: 'Pedir análisis',
    analistaRepetir: 'Repetir análisis',
    analistaPensando: 'Leyendo el mazo y el meta…',
    analistaEspera:
      'El analista revisa los torneos de los últimos 14 días: puede tardar medio minuto.',
    analistaBloqueado:
      'Espera al analista: si la lista cambia a mitad del análisis, el resultado ya no hablaría de tu mazo.',
    analistaNecesita60: 'El análisis se pide con la lista completa: 60 cartas.',
    analistaConfianza: 'Confianza',
    analistaConf_alta: 'alta',
    analistaConf_media: 'media',
    analistaConf_baja: 'baja',
    analistaCached: 'análisis vigente para el meta actual',
    analistaComo: 'Leído como',
    analistaEsperado: 'victoria esperada hoy',
    analistaC_consistencia: 'Consistencia',
    analistaC_estructura: 'Estructura',
    analistaC_meta: 'Posicionamiento',
    analistaC_estrategia: 'Estrategia',
    analistaC_cartas: 'Elección de cartas',
    analistaC_resiliencia: 'Resiliencia',
    analistaSinMatriz:
      'Mazo fuera del meta registrado: el posicionamiento es lectura del analista, no matriz de torneos, y la confianza baja.',
    analistaFortalezas: 'Fortalezas',
    analistaDebilidades: 'Debilidades',
    analistaSugerencias: 'Cambios sugeridos',
    analistaAplicar: 'Aplicar cambio',
    analistaAplicarTodo: 'Aplicar sugerencias',
    analistaAplicada: 'Aplicado',
    analistaDeshacer: 'Deshacer',
    analistaTope:
      'El tope de 4 copias por nombre recortó la sugerencia ({cards}): el mazo se mantiene legal.',
    analistaAplicarParcial:
      'Solo una parte de este cambio se reconoció como carta; se aplica lo reconocido.',
    analistaAplicadoNota:
      'Cambios aplicados y guardados en la lista. La nota corresponde a la lista anterior: vuelve a pedir análisis cuando termines.',
    analistaMatchups: 'Sobre los matchups',
    analistaError: 'El analista no pudo responder. Prueba de nuevo en un momento.',
    analistaLimite: 'Se agotaron los análisis de esta hora; vuelve en un rato.',
    analistaHint:
      'Todos los números citados salen de torneos reales de los últimos 14 días; el analista no estima cifras. La nota combina métricas calculadas (consistencia, estructura, posicionamiento) con el juicio del analista (estrategia, cartas, resiliencia). Ese juicio es una opinión generada por IA sobre datos parciales — muestras pequeñas en algunos matchups, un meta que rota y ningún contexto de tus partidas — y puede equivocarse: tómalo como una segunda opinión, no como una verdad.',
    analistaSugerenciasAviso:
      'Ideas para probar, no mejoras garantizadas: nada obliga a aplicarlas y la decisión final sobre tu mazo es tuya.',
    exportedList: 'Lista para Pokémon TCG Live',
    missingSetCode:
      'Sin código de expansión conocido (revisa estas líneas en el juego)',
    navMeta: 'Meta',
    // Con las palabras que la gente busca ("meta pokemon tcg live"), no el
    // término de jerga: este encabezado es la señal on-page más fuerte de la
    // portada y "Metajuego" a secas no decía ni el juego.
    metaTitle: 'Meta de Pokémon TCG',
    metaTagline: 'La tier list del formato Standard con datos de torneos reales — el mismo meta que juegas en Pokémon TCG Live.',
    metaTournaments: 'torneos',
    metaPlayers: 'jugadores',
    metaGames: 'partidas',
    deckNoGames: 'Sin partidas aún',
    deckTrackerOn: 'En el tracker',
    deckTrackerUse: 'Utilizar en el tracker',
    privacyLink: 'Privacidad y datos',
    desktopAppLink: 'App de escritorio (Windows) · beta',
    desktopAppNote: 'Tracker en vivo para PTCG Live, en beta: se mejora día a día y puede fallar en configuraciones aún no probadas. Requiere el juego en proporción 16:9 — recomendado 1920×1080 a pantalla completa, que es la configuración probada.',
    signUpAccept: 'Al crear tu cuenta aceptas la {link}.',
    signUpAcceptLink: 'política de privacidad y datos',
    pgLabel: 'Páginas del ranking',
    pgPrev: 'Página anterior',
    pgNext: 'Página siguiente',
    metaLive: 'EN VIVO',
    donutGlobal: 'global',
    reprocessMatch: 'Reanalizar',
    reprocessHint: 'Vuelve a leer el registro con el lector actual y corrige los datos que salieran mal.',
    reprocessedOk: 'Partida reanalizada con el lector actual.',
    // --- Mazos de la comunidad ---
    energiaGrupo: 'Energía',
    decksMine: 'Mis mazos',
    decksCommunity: 'Comunidad',
    communityTitle: 'Mazos de la comunidad',
    communityHint: 'Compartidos por otros jugadores: cópialos, pruébalos y vota los que te ganen.',
    communityEmptyTitle: 'La vitrina está vacía',
    communityEmpty: 'Sé quien la estrene: abre uno de tus mazos y pulsa Compartir.',
    communityBy: 'por {name}',
    communityAnon: 'un entrenador anónimo',
    communityTop: 'Top',
    communityNew: 'Nuevos',
    communityLike: 'Me gusta',
    voteNeedsAccount: 'Entra con tu correo verificado para votar.',
    copyToMine: 'Copiar a mis mazos',
    copiedToMine: 'Copiado a tus mazos.',
    shareDeck: 'Compartir',
    unshareDeck: 'Dejar de compartir',
    sharedBadge: 'Compartido',
    sharedOkAviso: 'Compartido con la comunidad. ¡Que lo prueben!',
    unsharedAviso: 'El mazo ya no está compartido.',
    reshareHint: 'Ya está compartido: volver a compartir publica la lista actual.',
    shareDuplicate: 'Ya hay un mazo idéntico en la comunidad: «{name}» de {author}. Cambia alguna carta para publicar el tuyo.',

    /* --- Catálogo de mazos públicos y detalle compartido ---------------- */
    publicDecksTitle: 'Mazos públicos',
    publicDecksHint:
      'Las listas que se jugaron en torneo y los mazos que comparte la gente, en la misma tabla: qué lleva cada uno, cuánto cuesta y cómo le fue.',
    publicSearchPlaceholder: 'Buscar por mazo, jugador o torneo…',
    publicSource: 'Origen',
    publicAll: 'Todos',
    publicTournament: 'De torneo',
    publicCommunity: 'De la comunidad',
    anyArchetype: 'Cualquier arquetipo',
    publicBestPerArchetype: 'Solo la mejor de cada arquetipo',
    publicCount: '{n} mazos',
    publicNoMatchTitle: 'Ningún mazo casa con eso',
    publicNoMatch: 'Prueba con menos filtros o con otro término.',
    publicDeckCol: 'Mazo',
    publicAuthor: 'Creador',
    publicCostCredits: 'Créditos',
    publicCostClp: 'Pesos',
    publicGames: 'Partidas',
    publicCountry: 'País',
    publicViewShort: 'Ver',
    draftHint: 'Sin guardar — vive en este navegador',
    draftDiscard: 'Descartar',
    deckEmptyNoSave: 'Un mazo vacío no se guarda: añade alguna carta primero.',
    publicRounds: 'Rondas',
    publicRoundsHint: 'Las rondas que jugó este jugador en ese torneo. El techo son las rondas del torneo (14 el más largo), no las partidas del arquetipo.',
    publicMetaShare: 'Cuota',
    publicShared: 'Compartido por un jugador',
    publicUnknownRarity: '{n} cartas sin tarifa conocida: el total se queda corto.',
    publicMissingPrices: '{n} cartas sin precio en Chile: el total se queda corto.',
    publicAggregatePrices: '{n} cartas cotizadas con un precio agregado, sin idioma verificado.',
    publicLowSample: 'pocas',
    publicLowSampleHint: 'Solo {n} partidas: el porcentaje aún no dice mucho.',
    publicCore: 'Núcleo',
    publicCopyShort: 'Copiar',
    edit: 'Editar',
    exportShort: 'Exportar',
    addCards: 'Añadir cartas',
    pasteDeckHere: 'Pega aquí la lista copiada del juego…',
    publicLoadFailed: 'No se pudieron cargar los mazos.',
    publicBack: 'Volver a la lista',
    publicYours: 'Un mazo tuyo',
    publicList: 'La lista',
    publicNumbers: 'Los números',
    publicRecordRow: 'Récord',
    publicArchWinRate: 'Victorias del arquetipo',
    publicNGames: '{n} partidas',
    publicLooksLike: 'Se parece a {name} en un {n}%.',
    publicComposition: 'De qué está hecho',
    publicOddsPro:
      'Las probabilidades de apertura son de Pro, aquí y en tus propios mazos.',
    prizeLoad: 'Carga de premios',
    prizeWorst: 'un noqueo cuesta hasta {n}',
    prizeOne: '1 premio',
    prizeTwo: '2 premios',
    prizeThree: '3 premios',
    catPokemon: 'Pokémon',
    catTrainer: 'Entrenador',
    catEnergy: 'Energía',
    catSupporter: 'Partidario',
    catItem: 'Objeto',
    catTool: 'Herramienta',
    catStadium: 'Estadio',
    metaDelayed: 'META DE HACE {n} DÍAS',
    metaDelayedHint:
      'El plan gratis ve el metajuego con una semana de retraso. Con Pro lo ves al día, según llegan los torneos.',
    metaUpdatedAgo: 'actualizado hace {t}',
    haceMin: '{n} min',
    haceHoras: '{n} h',
    metaWindow: 'Periodo',
    days7: 'Última semana',
    days30: 'Últimos 30 días',
    days90: 'Últimos 90 días',
    daysAll: 'Todo',
    archetype: 'Arquetipo',
    archDetailToggle: 'Ver detalle de {name}',
    share: 'Presencia',
    winRateShort: 'Victorias',
    games: 'partidas',
    coreCards: 'Cartas del arquetipo',
    lists: 'listas',
    rank: '#',
    allFormats: 'Todos los formatos',
    edge: 'Infravalorado',
    edgeHint:
      'Victorias por encima del 50 % menos la presencia: alto = gana más de lo que se juega.',
    sortBy: 'Ordenar por',
    sortAsc: 'ascendente',
    sortDesc: 'descendente',
    conversion: 'Top 8',
    conversionHint:
      'Cuota de entradas con ese mazo que acabaron en el top 8. Cuenta también a quien abandonó a medias: entró con ese mazo. Acota el tamaño de torneo para que el número sea comparable.',
    minTournament: 'Tamaño de torneo',
    anySize: 'Cualquiera',
    playersN: '{n}+ jugadores',
    minSample: 'Muestra mínima',
    minSampleAny: 'Sin mínimo',
    gamesN: '{n}+ partidas',
    archetypesShown: '{n} de {total} arquetipos',
    matchups: 'Emparejamientos',
    minGames: 'mínimo por celda:',
    matchupLegend:
      'Cada celda es el porcentaje de victorias de la fila contra la columna. Un punto significa muestra insuficiente.',
    metaEmpty: 'Sin datos de metajuego.',
    metaEmptyHint: 'Ejecuta la ingesta con: npm run ingest:meta -w server',
    metaNoData: 'No hay suficientes partidas en este periodo.',
    data: 'Datos',
    dataStatus: 'Estado de los datos',
    autoNote:
      'Se actualizan solos: cartas cada semana, meta cada día. Precios y tipo de cambio se piden en vivo.',
    updateNow: 'Actualizar',
    updating: 'Actualizando…',
    never: 'nunca',
    justNow: 'hace menos de 1 h',
    daysAgo: 'días',
    lastRunFailed: 'falló la última vez',
    paneArchetypes: 'Arquetipos',
    paneMatchups: 'Matriz completa',
    /* El rótulo corto de la subpestaña en el teléfono: en la fila a mitades,
       "Matriz completa" se partía en dos líneas y estiraba la fila entera.
       Mismo arreglo que navMatchesShort en el dock. */
    paneMatchupsShort: 'Matriz',
    paneUpcoming: 'Próximos',
    tourPasados: 'Ya jugados',
    upcomingRegion: 'Región',
    upcomingLatam: 'Latinoamérica',
    upcomingChile: 'Chile',
    upcomingOnline: 'Online',
    upcomingTodos: 'Todos',
    upcomingSoloAbiertos: 'Con inscripción abierta',
    upcomingOficial: 'Oficial',
    upcomingComunidad: 'Comunidad',
    upcomingApuntarse: 'Inscribirse',
    upcomingVerEvento: 'Ver evento',
    upcomingInscritos: '{n} inscritos',
    upcomingVacio: 'Nada a la vista por aquí',
    upcomingVacioHint: 'Con estos filtros no hay torneos anunciados. Prueba a mirar todos.',
    upcomingFallo: 'No se pudo leer el calendario de torneos.',
    upcomingFuente: 'Calendario de limitlesstcg.com y play.limitlesstcg.com. Las inscripciones se hacen en su web.',
    paneTournaments: 'Torneos',
    filterTrainerType: 'Entrenador',
    filterMark: 'Marca reg.',
    subAllCards: 'Todas las cartas',
    subSets: 'Expansiones',
    setNCards: '{n} cartas',
    setPlayedPct: '{n} % se juega en torneos',
    metaTrend: 'Tendencia',
    metaTrendWeeks: 'últimas {n} semanas',
    perfilTournaments: 'torneos',
    perfilRecord: 'Récord',
    perfilTop8: 'top 8',
    perfilFavDeck: 'Mazo habitual',
    perfilRecent: 'Resultados recientes',
    perfilPlaceOf: '{p}.º de {n}',
    /* Cuando el puesto ya lo dice la medalla, el texto solo pone el campo: si
       no, se lee "1 1.º de 137" con el uno repetido. */
    perfilOutOf: 'de {n}',
    panePlayers: 'Jugadores',
    playersTournaments: 'Torneos',
    playersFirsts: '1.º',
    playersHint: 'Ordenado por top 8, títulos y victorias en el periodo elegido.',
    searchPlayer: 'Buscar jugador…',
    playersShown: '{n} de {total} jugadores',
    playersSearchEmpty: 'Ningún jugador casa con «{q}».',
    tourStandingsTab: 'Clasificación',
    tourRoundsTab: 'Rondas',
    roundShort: 'R{n}',
    topRoundShort: 'Top · R{n}',
    tourTie: 'Empate',
    tourBye: 'Bye',
    filterAbility: 'Habilidad',
    abilityYes: 'Con habilidad',
    filterArtist: 'Ilustrador',
    addMatch: 'Registrar partida',
    historyTitle: 'Historial de partidas',
    moreFilters: 'Más filtros',
    moreActions: 'Más',
    collapseForm: 'Plegar el registro de partidas',
    otherElement: 'Otros',
    onlyThisElement: 'Ver los mazos de este elemento',
    showAllElements: 'Cerrar',
    seeMyDeck: 'Ver mi mazo',
    seeMyDeckHint: 'Ver la lista del mazo con el que jugaste esta partida',
    autoTag: 'auto',
    autoTagHint: 'Etiquetado automáticamente por la evidencia de la partida (confianza alta). Cualquier cambio manual lo convierte en tuyo.',
    tourPlayers: '{n} jugadores',
    tourLists: '{n} listas',
    tourWinner: 'Campeón',
    tourPlayer: 'Jugador',
    tourRecord: 'Récord',
    tourDeck: 'Mazo',
    tourViewList: 'Ver lista',
    tourTop: 'Top {n} de {total} jugadores',
    tourNoLists: 'Este torneo no publicó listas.',
    bestMatchups: 'Emparejamientos favorables',
    worstMatchups: 'Cuesta arriba',
    searchArchetype: 'Buscar arquetipo…',
    archSearchEmpty: 'Ningún arquetipo casa con «{q}».',
    localMarketRef: 'referencia del mercado local',
    deckStatsProTitle: 'Las estadísticas del mazo son de Pro',
    deckStatsProBody: 'Mulligan, mano inicial, premios y la comparación contra el meta.',
    gateDeckStatsTitle: 'Análisis del mazo, con Pro',
    gateDeckStatsBody:
      'La revisión de legalidad es gratis. Con Pro se abren las probabilidades — mulligan, mano inicial, estar en premios — y la comparación de tu lista contra el meta.',
    proDeckStats: 'Estadísticas de tu mazo: apertura, premios y contra el meta',
    gateAnalistaTitle: 'El analista, con Pro',
    gateAnalistaBody:
      'Una evaluación de tu mazo hecha con IA y anclada a torneos reales: nota con rúbrica, fortalezas, debilidades y cambios sugeridos con evidencia. Cada análisis consulta datos de los últimos 14 días.',
    proAnalista: 'El analista: tu mazo evaluado con IA y datos de torneos',
    expectedVsField: 'Victoria esperada contra el meta actual:',
    fieldCovered: 'cubre',
    deckCheck: 'Revisión del mazo',
    chkMissing: 'Faltan {n} cartas para llegar a 60.',
    chkOver: 'Sobran {n} cartas: el mazo debe tener exactamente 60.',
    chkNoBasics: 'No hay ningún Pokémon Básico. Con esta lista no se puede empezar la partida.',
    chkMaxCopies: 'Llevas {n} copias de {card}. El máximo son 4.',
    chkAceSpec: 'Llevas {n} cartas ACE SPEC ({cards}). Solo se permite 1 por mazo.',
    chkRotation: 'En la próxima rotación este mazo pierde {n} cartas (marca {marca}): {cards}.',
    dockSeeDeck: 'Ver mazo',
    dockSeeSearch: 'Buscar cartas',
    deckCostTitle: 'Coste del mazo',
    costListTitle: 'Lista de compra',
    costNoPrice: 'sin precio local',
    handSimTitle: 'Mano inicial',
    handDraw: 'Robar 7 cartas',
    handRedraw: 'Robar otra mano',
    handMulligan: 'Mulligan: sin Pokémon Básico. En la mesa se enseña, se baraja y se roba de nuevo.',
    communityCredits: 'créditos (fabricar)',
    communityBuyCost: 'comprarlo (aprox.)',
    pasteFromClipboard: 'Pegar del portapapeles',
    clipboardEmpty: 'El portapapeles está vacío.',
    clipboardDenied: 'El navegador no dejó leer el portapapeles: pega con Ctrl+V (o mantén pulsado y «Pegar»).',
    tagOther: 'Otro (escribirlo)…',
    tagOtherPlaceholder: 'Nombre del mazo rival',
    addToDeck: 'Añadir al mazo',
    addedToDeck: '{card} añadida al mazo.',
    pasteFromGame: 'Importar mazo',
    pastedDeck: 'Lista pegada: {n} cartas en el mazo.',
    pasteNoDeck: 'El portapapeles no trae una lista del juego. Copia el mazo en PTCG Live y vuelve a intentarlo.',
    confirmPasteDeck: '¿Sustituir el mazo por la lista pegada?',
    confirmPasteDeckDetail: 'Las {n} cartas actuales se reemplazan por las del portapapeles.',
    pasteAction: 'Sustituir',
    pasteUnresolved: '{n} líneas no se reconocieron y quedaron fuera.',
    addedToDraft: 'Añadida al borrador ({n} cartas). Está en Mazos, como «Sin guardar».',
    addLimit: 'Ya llevas 4 copias de {card}.',
    addFailed: 'No se pudo añadir la carta.',
    trendTitle: 'Tendencia de precio',
    trendClp: 'mínimo en Chile · CLP',
    trendUp: 'Sube {p} %',
    trendDown: 'Baja {p} %',
    trendFlat: 'Estable',
    trendKeys: 'Flechas izquierda y derecha para recorrer los días.',
    pricesIntl: 'Referencia internacional',
    metaHowHint: 'Torneos Standard de Limitless con 32+ jugadores, cada 24 h. Toca para ver cómo se calcula cada cifra.',
    metaGoTournaments: 'Ver los torneos jugados',
    metaGoPlayers: 'Ver el ranking de jugadores',
    metaGoMatchups: 'Ver la matriz de enfrentamientos',
    /* En un mazo propio o de la comunidad la hoja se abre para llevarse la
       FOTO; "ver mazo completo" es el rotulo del meta, donde el mazo aun no
       se ha visto entero. */
    viewDeckPhoto: 'Ver foto',
    chkMulligan: 'Con {basics} Básicos, 1 de cada {n} manos empieza en mulligan. El 90% de las listas de torneo llevan {referencia} o más.',
    oddsAfter60: 'Las probabilidades aparecen con el mazo completo. Faltan {n} cartas.',
    oneEvery: '1 de cada {n} manos',
    ofSixty: 'de 60 cartas',
    card: 'Carta',
    deckOdds: 'Probabilidades de apertura',
    mulliganRate: 'Mulligan',
    basicsCount: 'Básicos',
    openingHand: 'Mano inicial',
    prizedOdds: 'A premios',
    /* Rótulos de las dos columnas de probabilidad de la lista del detalle:
       sobre una columna de cuatro cifras no caben "Mano inicial" ni
       "A premios" enteros. No son otro nombre, son el mismo acortado. */
    oddsColMano: 'Mano',
    oddsColPremio: 'Premios',
    oddsHint:
      'Calculado de forma exacta, no simulado. "A premios" es la probabilidad de que al menos una copia quede entre las 6 cartas de premio.',
    oddsIncomplete: 'La lista aún no tiene 60 cartas: los números son orientativos.',
    mulliganHigh: 'Pocos Básicos para este tamaño de mazo.',
    navMatches: 'Mis partidas',
    /* El rótulo del dock del teléfono, donde cada pestaña dispone de ~68 px:
       "Mis partidas" se partía en dos líneas y estiraba la barra entera. No es
       otro nombre, es el mismo acortado -- y de paso el dock queda con cinco
       sustantivos sueltos, que se leen mejor de un vistazo. */
    navMatchesShort: 'Partidas',
    pasteLog: 'Pegar registro de batalla',
    modeLog: 'Pokémon TCG Live',
    /* Corto a proposito: en movil las dos pastillas se reparten el ancho a
       mitades y el parentesis de "(mazos fisicos)" rompia la simetria. El
       propio formulario ya explica que es para partidas de mesa. */
    modeManual: 'Manual',
    recordOrigin: 'Origen',
    manualTitle: 'Apuntar una partida física',
    manualHint: 'Para partidas de mesa: quién ganó, el mazo de cada uno y, si te acuerdas, los premios que tomó cada lado.',
    iWon: 'Gané',
    iLost: 'Perdí',
    prizesMine: 'Premios que tomé',
    prizesTheirs: 'Premios del rival',
    opponentOptional: 'Nombre del rival (opcional)',
    manualNeedsResult: 'Elige quién ganó para poder guardar.',
    manualSaveFailed: 'No se pudo guardar la partida.',
    manualMatchDetail: 'Partida física apuntada a mano: no hay registro turno a turno.',
    pasteLogHint:
      'En Pokémon TCG Live, al terminar la partida pulsa BATTLE LOG en la pantalla de victoria o derrota y luego el icono de exportar. Se copia al portapapeles y se pierde al volver al menú, así que pégalo antes de salir.',
    saveMatch: 'Guardar partida',
    saving: 'Guardando…',
    whichAreYou: '¿Cuál de los dos eres? Solo se pregunta una vez.',
    playingAs: 'Juegas como',
    change: 'cambiar',
    aliasFilter: 'Alias de PTCGL',
    aliasAll: 'Todos',
    profileLabel: 'Perfil de juego',
    profileNone: 'Sin perfil',
    matchSaved: 'Partida guardada.',
    matchDuplicate: 'Esa partida ya estaba guardada.',
    matchLogHeader: 'Tu registro empieza con: «{linea}». Si el juego está en un idioma o variante regional que aún no cubrimos, mándanos esa línea y la añadimos.',
    matchNotALog: 'Eso no parece un registro de batalla de Pokémon TCG Live.',
    record: 'Balance',
    // "Empezando tú" y no "yendo primero": misma familia que el detalle
    // ("Empezaste tú") y sin el eco de posicion de carrera.
    goingFirst: 'Empezando tú',
    opponent: 'Rival',
    myDeckCol: 'Mi mazo',
    noDeck: 'Sin mazo',
    acceptDeckSuggestionHint: 'Deducido de tus Pokémon en la partida. Pincha para aplicarlo.',
    byDeck: 'Por mazo',
    winsOfGames: '{wins} de {games}',
    unlinkedMatches: '{n} partidas sin mazo asociado: no cuentan en estas cifras.',
    unlinkedMatchesOne: '1 partida sin mazo asociado: no cuenta en estas cifras.',
    theirDeck: 'Su mazo',
    result: 'Resultado',
    prizes: 'Premios',
    turnsShort: 'Turnos',
    dateCol: 'Fecha',
    // Numera lo que se ve (1 = primera fila mostrada). La cabecera es "N.º"
    // para no robar ancho; el nombre completo va en el title.
    numCol: 'N.º',
    numColFull: 'Número de fila',
    /*
     * "Primer turno: tuyo/suyo" y no "saliste primero/segundo": primero y
     * segundo suenan a podio de carrera, y "tuyo/suyo" es exactamente como el
     * detalle etiqueta cada turno. Un solo vocabulario para el mismo concepto.
     */
    wentFirstCol: 'Primer turno',
    // Version corta para la tarjeta movil: la larga no cabe en un cuarto de fila.
    wentFirstShort: '1er turno',
    // Como termino la partida. "Cierre" y no "tipo de victoria": tambien
    // aplica a las derrotas, y cabe en la tarjeta movil.
    endViaCol: 'Cierre',
    endPrizes: 'Premios',
    // "Rendición" y no "concesión": es como lo dice el propio juego en
    // español ("se rindió") y como se dice en la mesa.
    endConcession: 'Rendición',
    endBench: 'Sin banca',
    wentFirstYes: 'Tuyo',
    wentFirstNo: 'Suyo',
    datePlayed: 'Fecha de la partida',
    editDate: 'Cambiar la fecha',
    calPrevMonth: 'Mes anterior',
    calNextMonth: 'Mes siguiente',
    calToday: 'Hoy',
    copyLog: 'Copiar registro',
    exportCopied: 'Lista copiada: pégala en Pokémon TCG Live al importar un mazo.',
    exportBelow: 'Lista generada abajo, con su botón de copiar.',
    notifBell: 'Notificaciones',
    notifBellUnread: 'Notificaciones: {n} sin leer',
    notifTitle: 'Notificaciones',
    notifEmpty: 'Nada nuevo por aquí.',
    notifVoto: 'A alguien le gustó tu mazo «{mazo}».',
    notifMeta: 'El meta se actualizó: {n} torneos en la última semana.',
    notifNuevoUsuario: 'Cuenta nueva: {quien}.',
    notifMazoCompartido: '{autor} compartió «{mazo}» en la comunidad.',
    notifBuzonBug: 'Nuevo bug en el buzón de sugerencias.',
    notifBuzonIdea: 'Nueva sugerencia en el buzón.',
    notifSuscripcion: 'Suscripción de {correo}: {estado}.',
    notifClear: 'Limpiar',
    notifAhora: 'ahora mismo',
    notifHaceMin: 'hace {n} min',
    notifHaceHoras: 'hace {n} h',
    notifHaceDias: 'hace {n} días',
    win: 'Victoria',
    loss: 'Derrota',
    tie: 'Empate',
    iTied: 'Empaté',
    first: 'primero',
    second: 'segundo',
    noMatches: 'Aún no has registrado ninguna partida',
    noMatchesHint: 'Pega el registro de una partida de Pokémon TCG Live y se lee sola, o apunta a mano una partida en físico.',
    // Hoja de registro oficial para ligas y torneos (HojaRegistro.jsx).
    regButton: 'Hoja de torneo',
    regTitle: 'Hoja de registro para torneo',
    regIntroDeck: 'La planilla oficial de Play! Pokémon con «{name}», lista para imprimir o guardar como PDF.',
    regIntroPaste: 'Pega o escribe tu lista —como la exporta Pokémon TCG Live, o a mano: «4 Abra TWM 80»— y sale la planilla oficial lista para imprimir o guardar como PDF.',
    regPasteLabel: 'Tu lista',
    regGenerate: 'Generar hoja',
    regRegenerate: 'Actualizar hoja',
    regPlayer: 'Tus datos',
    regName: 'Nombre y apellidos',
    regBirth: 'Fecha de nacimiento',
    regDivision: 'División {div} en la temporada {season}.',
    regDivisionNone: 'Con tu fecha de nacimiento se marca sola la división.',
    regDiv_junior: 'Junior',
    regDiv_senior: 'Senior',
    regDiv_masters: 'Masters',
    regFormat: 'Formato',
    regPrivate: 'Tus datos se quedan en este navegador: no se envían ni se guardan con el mazo.',
    regNot60: 'La lista suma {n} cartas, no 60.',
    regUnresolved: '{n} línea(s) no casan con el catálogo y van tal como las escribiste:',
    regRenamed: '{n} carta(s) van con su nombre en inglés; revisa que sea la que querías:',
    regIgnored: '{n} línea(s) no se entendieron y quedaron fuera:',
    regPrint: 'Imprimir o guardar PDF',
    regPdfHint: 'Para el PDF, elige «Guardar como PDF» como impresora.',
    regPreviewEmpty: 'Aquí aparecerá la hoja cuando generes tu lista.',
    regFailed: 'No se pudo preparar la hoja. Inténtalo de nuevo.',
    viewDeckSheet: 'Ver mazo completo',
    deckSheetHint: 'Cartas enteras con su cantidad, para fotografiar o llevar a comprar.',
    downloadPng: 'Descargar PNG',
    generating: 'Generando…',
    print: 'Imprimir',
    pngFailed: 'No se pudo generar la imagen. Puedes fotografiar la pantalla o imprimir.',
    // Hoja del mazo: es UNA lista real, no el promedio del arquetipo. Decirlo
    // evita leer como contradiccion lo que en realidad es la informacion util.
    sheetIsOneList: 'Lista real de {player}',
    sheetIsOneListPlain: 'Una lista real, no el promedio del arquetipo',
    sheetPlacing: 'puesto {n}',
    sheetPickList: 'Listas reales del arquetipo — elige cuál ver',
    inclusionOf: 'en el {pct} de las listas',
    techPick: 'Elección personal',
    techPickHint:
      'La lleva menos de un cuarto de las listas: es de este jugador, no del arquetipo.',
    coreMissing: 'Núcleo que esta lista NO lleva',
    coreMissingHint:
      'Cartas que la mayoría del arquetipo juega y esta lista deja fuera. Suele ser lo más revelador.',
    openCardHint: 'Pincha una carta para ver su ficha.',
    // --- Planes ---
    gateDecksTitle: 'Guarda todos los mazos que quieras',
    gateDecksBody:
      'El plan gratis guarda hasta {limit} mazos. Con Pro guardas los que quieras y los tienes en cualquier dispositivo.',
    gateMatrixTitle: 'La matriz completa es de Pro',
    gateMatrixBody:
      'Cruza todos los arquetipos entre sí para preparar un torneo. Los emparejamientos favorables y cuesta arriba de cada mazo siguen siendo gratis.',
    gateCommunityTitle: 'Comparte tus mazos con Pro',
    gateCommunityBody:
      'Publicar tus mazos, firmarlos con tu nombre y subir en el ranking de la comunidad es de Pro. Ver, copiar y votar los mazos compartidos es gratis.',
    gateMetaTitle: 'Meta completo con Pro',
    gateMetaBody:
      'El plan gratis muestra los 3 arquetipos más jugados, con una semana de retraso. Con Pro ves el campo entero, al día y en cualquier periodo.',
    gateMatchesTitle: 'Cada partida, con Pro',
    gateMatchesBody:
      'Guardas partidas y ves el resumen de tus mazos gratis. Con Pro se abre la lista completa, el detalle turno a turno de cada una y tu balance contra cada rival.',
    gateGenericTitle: 'Esto es de Pro',
    gateUsage: 'Llevas {current} de {limit}.',
    proDecks: 'Mazos ilimitados, sincronizados entre dispositivos',
    /* El meta es gratis desde jul-2026 (plans.js): la promesa Pro alrededor
       del meta pasa a ser llevárselo al juego, no verlo. */
    proMeta: 'Exportación de mazos del meta al formato de Pokémon TCG Live',
    proHistory: 'Historial de partidas sin límite',
    proTracker: 'Seguimiento de la partida en vivo (en construcción)',
    proSoon: 'Los pagos aún no están activos. Escríbenos si quieres probarlo antes.',
    goPro: 'Hacerme Pro por ${price}/mes',
    tagDeck: 'Etiquetar',
    untagged: 'Sin etiquetar',
    myMatchups: 'Cómo te va contra cada mazo',
    myMatchupsHint: 'Etiqueta el mazo del rival en tu historial y aquí verás tu balance junto a lo que ese arquetipo hace en el meta.',
    filterByDeck: 'Acotar por mazo',
    deckLabel: 'Mazo',
    decksNoGames: 'Sin partidas:',
    /* La condicion VA EN LA PROMESA: el trial se estrena al confirmar el
       correo, y prometer "gratis al crear cuenta" dejaba un hueco entre
       registrarse y confirmar en el que el meta seguia recortado y la promesa
       parecia incumplida. */
    gateSignUp: 'Crea tu cuenta — 3 días de Pro al confirmar tu correo',
    seeAllProTrial: 'Ver todo — 3 días gratis',
    matchDeleted: 'Partida borrada.',
    deckDeleted: 'Mazo borrado.',
    savedToMyDecks: 'Guardado en tus mazos.',
    matrixHowTo: 'Cada celda: % de victorias del mazo de la FILA contra el de la columna. La celda espejo cuenta las mismas partidas desde el otro lado, por eso 60 % aquí es ~40 % allí (los empates hacen que no sume 100 exacto).',
    matrixCorner: 'Filas: tu mazo. Columnas: el rival.',
    matrixCellTip: '{a} gana el {pct} % de {n} partidas contra {b}',
    matrixPairTip: '{a} {pa} % · {b} {pb} % — mismas {n} partidas',
    matrixNoSample: '{a} contra {b}: solo {n} partidas, muestra insuficiente',
    /* --- La matriz en el telefono ------------------------------------------
       Una tabla de 10x10 no cabe en 375 px: se lee eligiendo TU mazo y
       mirando la lista de rivales. La escala va en palabras ademas de en
       color, que es lo unico que sirve a quien no distingue el rojo del
       verde -- y tambien a quien mira el movil al sol. */
    mxPickDeck: 'Mi mazo',
    mxWorstFirst: 'Peores primero',
    mxBestFirst: 'Mejores primero',
    mxVeryBad: 'Muy desfavorable',
    mxBad: 'Desfavorable',
    mxEven: 'Parejo',
    mxGood: 'Favorable',
    mxVeryGood: 'Muy favorable',
    mxNoData: 'Datos insuficientes',
    mxRowAria: '{a} contra {b}: {p} % en {n} partidas, {v}',
    mxRowAriaNoData: '{a} contra {b}: datos insuficientes, solo {n} partidas',
    logTruncated: 'El registro llegó cortado: no incluye el final de la partida, así que el resultado no se puede deducir. Suele pasar al copiarlo antes de que el juego escriba el cierre.',
    editDeck: 'Editar mazo',
    doneEditing: 'Terminar edición',
    fnModalTitle: 'Todo lo que hace PokeTrack',
    seeAllFeatures: 'ver todo lo que hace →',
    fnModalSub: 'Meta actualizado, mazos y todo el análisis de tus partidas de Pokémon TCG Live.',
    fnCardsTitle: 'Biblioteca completa de cartas',
    fnCardsBody: 'Más de 4.700 cartas en español e inglés, con búsqueda por nombre, ataque o efecto, filtros por tipo y formato, todas las versiones de cada carta, y precios de Cardmarket, TCGplayer y tiendas de Chile.',
    fnMetaTitle: 'Rankings del meta, siempre al día',
    fnMetaBody: 'Los arquetipos que dominan el competitivo, con su cuota de campo, victorias y emparejamientos, calculados de torneos reales y actualizados a diario. Filtrable por formato y periodo.',
    fnDecksTitle: 'Constructor y análisis de mazos',
    fnDecksBody: 'Monta mazos a mano o importándolos del juego, con probabilidades de apertura, mulligan, sugerencias estadísticas y comparación contra el meta. Exporta cualquier mazo —tuyo o del meta— al formato que Pokémon TCG Live importa.',
    fnMatchesTitle: 'Rastreador de partidas de Pokémon TCG Live',
    fnMatchesBody: 'Pega el registro de batalla y la partida queda guardada: resultado, premios, turnos y el relato turno a turno. Estadísticas por mazo — victorias, empezando tú, turnos medios, diferencial de premios — y tu balance contra cada arquetipo rival.',
    fnRivalTitle: 'Identificación del mazo rival',
    fnRivalBody: 'Con los Pokémon que se le vieron al rival, PokeTrack deduce qué arquetipo jugaba y con cuánta certeza, entrenado con miles de listas de torneo. Funciona con el juego en español o en inglés.',
    fnProTitle: 'Y con Pro, sin límites',
    fnProBody: 'Mazos ilimitados, estadísticas del mazo (apertura, mulligan, premios y contra el meta), el analista — tu mazo evaluado con IA y datos de torneos reales: nota, debilidades y cambios sugeridos —, historial completo con detalle turno a turno, y exportación del meta al juego. Toda cuenta nueva estrena 3 días de Pro al confirmar el correo.',
    exportToGame: 'Exportar al juego',
    copyManually: 'El portapapeles no dejó copiar: selecciona y copia a mano.',
    savedToDecks: 'Guardado ✓',
    saveToMyDecks: 'Guardar en mis mazos',
    exportFailed: 'No se pudo exportar. Prueba otra vez.',
    gateMetaExportTitle: 'Llévate el meta al juego',
    gateMetaExportBody: 'Con Pro exportas cualquier mazo del meta en el formato que Pokémon TCG Live importa, o lo guardas en tus mazos para rastrear tus partidas con él.',
    otherPrints: 'Otras versiones ({n})',
    evolutionLine: 'Línea evolutiva',
    prevCard: 'Carta anterior',
    nextCard: 'Carta siguiente',
    newVersionReady: 'Hay una versión nueva de PokeTrack lista.',
    reloadNow: 'Recargar',
    battleStart: 'Comienza el combate contra {name}',
    battleEndWin: 'Fin del combate: ¡victoria!',
    battleEndLoss: 'Fin del combate: gana {name}',
    battleEndUnknown: 'Fin del combate: sin resultado reconocido',
    seeArchetype: 'ver su mazo',
    seeArchetypeHint: 'Abre este arquetipo en el Meta, con su lista agregada del campo.',
    avgTurns: 'Turnos medios',
    prizeDiff: 'Dif. premios',
    playedWith: 'Jugué con',
    autoDetected: 'detectado ({pct} %)',
    analyzingLog: 'Analizando el registro…',
    analyzing: 'Analizando…',
    onlyDeckMatches: 'Solo las {n} partidas de «{name}».',
    showAll: 'Ver todas',
    deckMatchesLink: 'Ver partidas',
    changeTag: 'Cambiar esta etiqueta',
    tagNotSaved: 'No se pudo guardar el cambio. Se ha dejado como estaba.',
    tagChanged: 'Etiqueta cambiada.',
    undoTo: 'Volver a «{name}»',
    mismatchSays: 'lo que se vio apunta a {name} ({pct} %)',
    mismatchHint: 'Los Pokémon que se le vieron al rival no encajan con esta etiqueta. Pulsa para corregirla.',
    deckMismatchHint: 'Los Pokémon que jugaste no encajan con este mazo. Pulsa para corregirlo.',
    unknownResult: 'Sin resultado',
    wentFirst: 'Empezaste tú',
    wentSecond: 'Empezó el rival',
    nTurns: '{n} turnos',
    nTurnOne: '1 turno',
    turnN: 'Turno {n}',
    yourTurn: 'tuyo',
    theirTurn: 'del rival',
    setupPhase: 'Preparación',
    matchDetail: 'Detalle de la partida',
    vsOpponent: 'Contra {name}',
    detailFailed: 'No se pudo cargar el detalle.',
    noLogStored: 'Esta partida se guardó antes de que se almacenara el registro, así que no hay turno a turno.',
    logUnparsed: 'El registro está guardado pero no se pudo interpretar.',
    nothingLogged: 'Solo robos, energías y entrenadores.',
    fullLog: 'Ver el registro completo',
    evAttack: '{pokemon} usa {move} contra {target} · {damage} de daño',
    evKo: '{card} cae derrotado',
    evPrize: 'toma {n} premios',
    evPrizeOne: 'toma 1 premio',
    evEvolve: 'evoluciona a {card}',
    evAbility: '{pokemon} usa {name}',
    evPlay: 'juega {card}',
    evConcede: 'se rinde',
    gameOne: 'partida',
    allDecks: 'Todos',
    lowSampleHint: 'Pocas partidas: este porcentaje se mueve mucho con cada resultado.',
    noMatchupsForDeck: 'Aún no hay partidas de este mazo con el rival etiquetado.',
    metaLabel: 'en el meta:',
    ofField: 'del campo',
    notInMeta: 'sin datos de meta',
    madeBy: 'Hecho por',
    footNote: 'Datos de torneos vía Limitless y cartas vía TCGdex. Proyecto no oficial: no está producido ni avalado por Nintendo ni por The Pokémon Company.',
    savedDecks: 'Mis mazos',
    deckName: 'Nombre del mazo',
    saveDeck: 'Guardar',
    savedOk: 'Guardado',
    unsavedChanges: 'Cambios sin guardar',
    newDeck: 'Nuevo',
    deleting: 'Borrando…',
    deleteFailed: 'No se pudo borrar el mazo. Inténtalo otra vez.',
    deleteDeck: 'Borrar',
    signInToSave: 'Entra para guardar tus mazos y tenerlos en cualquier dispositivo.',
    showingOf: 'Mostrando {n} de {total}',
    seeAllPro: 'Ver todo con Pro',
    matchListProTitle: 'Mira cada partida con Pro',
    matchListProBody: 'Tienes {n} guardadas. Con Pro abres la lista, el detalle turno a turno y tu balance contra cada rival.',
    adminTitle: 'Administración',
    adminUsers: 'Usuarios',
    adminVerified: 'verificados',
    adminDecks: 'Mazos',
    adminMatches: 'Partidas',
    adminSearch: 'Buscar por correo o nombre…',
    adminGrant: 'Dar Pro',
    adminRevoke: 'Quitar Pro',
    adminRevokeTitle: '¿Quitar Pro?',
    leaveUnsavedTitle: '¿Salir sin guardar?',
    leaveUnsavedDetail: 'Los cambios de este mazo se perderán.',
    leaveUnsavedAction: 'Salir sin guardar',
    adminRevokeDetail: '{who} pasará al plan gratis y dejará de ver lo de Pro. Se puede volver a dar cuando quieras.',
    adminPlanFailed: 'No se pudo cambiar el plan. Inténtalo de nuevo.',
    adminUnverified: 'sin verificar',
    adminNoUsers: 'Ningún usuario coincide.',
    adminAnalytics: 'Mis visitas en la analítica',
    adminAnalyticsIn: 'Tus visitas cuentan en las métricas como las de cualquier usuario.',
    adminAnalyticsOut: 'Esta cuenta está excluida: tus visitas no cuentan en las métricas, entres desde donde entres.',
    adminAnalyticsExclude: 'Excluirme',
    adminAnalyticsInclude: 'Volver a contar',
    navAdmin: 'Admin',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    fbTitle: 'Te escuchamos',
    fbIntro: 'PokeTrack mejora con lo que cuentan quienes lo usan: las sugerencias marcan el rumbo y los bugs que se cuelan sin querer se corrigen antes si alguien los avisa.',
    fbNeedsAccount: 'Para escribirnos necesitas una cuenta con el correo confirmado — así cada aviso viene de alguien real.',
    fbTypeLabel: 'Qué nos cuentas',
    fbBug: 'Un bug',
    fbSuggestion: 'Una sugerencia',
    fbPlaceholder: 'Cuéntanos qué pasó o qué te gustaría ver. Corto y directo sirve perfecto.',
    fbSend: 'Enviar',
    fbThanks: '¡Gracias! Lo leemos de verdad.',
    fbFailed: 'No se pudo enviar. Inténtalo de nuevo.',
    fbTooMany: 'Ya nos enviaste varios hoy; mañana podrás mandar más.',
    fbFootLine: 'Escuchamos a los usuarios:',
    fbFootCta: 'envíanos un bug o una sugerencia',
    fbAdminTitle: 'Buzón de sugerencias',
    fbAdminNew: 'sin leer',
    fbAdminDone: 'Atendido',
    fbAdminReopen: 'Reabrir',
    noDecklistYet: 'Este arquetipo aún no tiene lista guardada',
    fbAdminBack: 'Volver al panel',
    fbAdminAllRead: 'todo atendido',
    tallerTitle: 'Taller de mazos',
    tallerSub: 'contra el meta de hoy',
    imgCacheTitle: 'Caché de imágenes',
    imgCacheSub: 'de ~{objetivo} · {mb} MB',
    imgCacheVacia: 'aún sin guardar nada',
    imgCacheFallo: 'no puede escribir en disco',
    tallerOpen: 'Abrir taller',
    tallerBack: 'Volver al panel',
    tallerMeta: 'Foto del meta',
    tallerTournaments: 'torneos',
    tallerPlayers: 'jugadores',
    tallerWindow: 'ventana',
    tallerLastIngest: 'último ingreso',
    tallerMyDecks: 'Mis mazos',
    tallerHold: 'Sostener',
    tallerTune: 'Revisar',
    tallerRebuild: 'Rehacer',
    tallerArchetype: 'Arquetipo',
    tallerNoArchetype: 'Fuera del meta registrado',
    tallerExpectedWr: 'WR esperado',
    tallerWorst: 'Peores emparejamientos',
    tallerMissing: 'Núcleo que no lleva',
    tallerUnusual: 'Fuera del molde',
    tallerPrizesPerKo: 'premios por noqueo',
    tallerMegaWarn: 'Lleva ex de Evolución Mega: cada noqueo son 3 premios.',
    tallerOpportunities: 'Joyas ocultas',
    tallerOpportunitiesHelp: 'Arquetipos que ganan y que casi nadie juega. De aquí sale un mazo que el rival no espera.',
    tallerNoOpportunities: 'Hoy no hay ninguno que cumpla el filtro.',
    tallerGuide: 'Guía de mesa',
    tallerGuideEmpty: 'Sin guía todavía. Escribe el plan, la cuenta letal y con qué se gana.',
    tallerGuideAdd: 'Escribirla a mano',
    tallerGuideEdit: 'Editar',
    tallerGuideWrite: '✦ Escribir guía',
    tallerGuideRewrite: '✦ Rehacer guía',
    tallerGuideWriting: 'Escribiendo…',
    tallerGuideOverwrite: 'Ya hay una guía escrita. ¿La reemplazo?',
    tallerGuideOverwriteOk: 'Sí, rehacerla',
    tallerGuideLimit: 'Llegaste al límite de peticiones por ahora. Prueba en un rato.',
    tallerGuideFailed: 'No se pudo escribir la guía. Inténtalo de nuevo.',
    tallerGuidePlaceholder: 'Ej.: abre con Ferocious Bellow, no con el ataque grande. Fiery Big Bang 290 + Maximum Belt 50 = 340 contra ex.',
    tallerPrizeRuleTitle: 'Los Mega dan 3 premios',
    tallerPrizeRuleBody: 'Medio juego en un noqueo. Un mazo con Megas no puede comerciar golpe por golpe: el rival gana con dos noqueos y tú necesitas tres. O lo curas del todo, o lo escondes.',
    tallerField: 'Qué te vas a encontrar',
    tallerFieldHelp: 'El campo con el que se cruzan tus mazos. Un win rate esperado no dice lo mismo si el campo lo domina alguien a quien le ganas.',
    tallerShare: 'Cuota',
    tallerWinRate: 'Victorias',
    tallerTop8: 'Top 8',
    tallerAnalyse: 'Pedir análisis',
    tallerReadOnly: 'Este tablero solo lee: aplicar un cambio sigue siendo cosa del constructor.',
    preciosTitle: 'Vigía de precios',
    preciosOpen: 'Abrir vigía',
    preciosSub: 'chollos en tiendas chilenas',
    preciosKpiOpps: 'oportunidades · chollos en tiendas chilenas',
    preciosBack: 'Volver al panel',
    preciosLastRun: 'Última pasada',
    preciosNoRun: 'Aún sin pasada: lánzala desde Datos («Vigía de precios»).',
    preciosStoreProducts: '{total} productos · {ligadas} ofertas ligadas · {disponibles} en stock',
    preciosStoreError: 'la última pasada falló',
    preciosOpps: 'Oportunidades',
    preciosOppsHelp: 'Precios muy por debajo de lo normal, para comprar barato y revender al precio de siempre. «Normal» es la mediana de las otras tiendas hoy, o la de los últimos 30 días si nadie más la vende.',
    preciosNoOpps: 'Ahora mismo ninguna carta está lo bastante barata. El vigía sigue mirando 4 veces al día.',
    preciosCard: 'Carta',
    preciosBuyAt: 'Comprar en',
    preciosNormally: 'Normal',
    preciosSavings: 'Ahorro',
    preciosVsHistory: 'vs 30 días',
    preciosStore: 'Tienda',
    preciosFilterAllStores: 'Todas las tiendas',
    preciosFilterText: 'Filtrar por carta o expansión…',
    preciosNoFiltered: 'Ninguna oportunidad pasa estos filtros.',
    preciosGo: 'Ir a la tienda',
    preciosSearch: 'Comparar una carta por nombre…',
    preciosSearchHelp: 'Antes de comprar cualquier carta: quién la vende y a cuánto.',
    preciosSearchNone: 'Ninguna carta con oferta en tienda casa con esa búsqueda.',
    preciosOutOfStock: 'agotada',
    notifPrecio: '«{carta}» a ${precio} en {tienda} (normal ${referencia})',
    noArt: 'Ilustración aún no disponible',
    noArtCard: 'Aún no tenemos la ilustración de esta carta. La añadiremos en cuanto esté disponible.',
    cannotUndo: 'Esto no se puede deshacer.',
    sheetCraft: 'Fabricarlo:',
    sheetCraftValue: '~{n} créditos',
    sheetCraftHint: 'Créditos aproximados para fabricar esta lista en Pokémon TCG Live, según la rareza de cada carta. Las energías básicas son gratis.',
    sheetCraftUnknown: '{n} cartas quedan fuera por rareza sin tarifa conocida.',
    sheetBuy: 'Comprarlo:',
    sheetBuyHint: 'Suma del single disponible más barato por carta en el mercado chileno. Es una referencia: el stock y los precios cambian a diario.',
    sheetBuyMissing: '{n} cartas sin precio local no están sumadas.',
    sheetBuyAggregate: '{n} cartas se cotizan con un precio agregado (sin idioma ni condición verificados): el total puede quedarse corto.',
    adminDeleteUser: 'Eliminar cuenta',
    adminDeleteUserTitle: '¿Eliminar la cuenta {correo}?',
    adminDeleteUserDetail: 'Se borran también sus mazos ({mazos}), sus partidas ({partidas}) y su suscripción.',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    confirmDeleteMatch: '¿Eliminar esta partida?',
    confirmDeleteMatchDetail: 'Contra {rival} · {resultado} · {premios}',
    confirmDeleteDeck: '¿Eliminar el mazo «{nombre}»?',
    confirmClearDeck: '¿Vaciar la lista?',
    confirmClearDeckDetail: 'Se quitan las {n} cartas que tienes puestas.',
    clearAction: 'Vaciar',
    filterOptions: 'Buscar…',
    noOptions: 'Nada coincide.',
    acceptSuggestionHint: 'Deducido de los Pokémon que se le vieron. Pincha para aplicarlo.',
    save: 'Guardar',
    refreshDenied: 'Tu sesión caducó o no eres administrador. Vuelve a entrar.',
    needsSession: 'Entra con tu cuenta para guardar partidas.',
    needsSessionMatchesHint: 'Tu historial, tu balance por mazo y cómo te va contra cada arquetipo se guardan en tu cuenta, así que los tienes en cualquier dispositivo.',

    // --- Cuenta ---
    account: 'Cuenta',
    signIn: 'Entrar',
    signUp: 'Crear cuenta',
    signOut: 'Cerrar sesión',
    email: 'Correo',
    password: 'Contraseña',
    newPassword: 'Contraseña nueva',
    passwordMin: 'Mínimo 8 caracteres',
    yourName: 'Tu nombre',
    optional: 'opcional',
    inviteCode: 'Código de invitación',
    forgotPassword: '¿Olvidaste tu contraseña?',
    backToSignIn: 'Volver a entrar',
    recoverTitle: 'Recuperar contraseña',
    recoverHint: 'Te enviamos un enlace para elegir una contraseña nueva.',
    recoverSent:
      'Si ese correo tiene cuenta, el enlace ya va en camino. Revisa también el correo no deseado.',
    resetTitle: 'Elige una contraseña nueva',
    sending: 'Enviando…',
    // Por qué crear cuenta. Se dice lo que se gana, no lo que se bloquea.
    guestPitch:
      'Con una cuenta guardas tus mazos y tu historial de partidas, y son sólo tuyos.',
    guestBrowsing: 'Puedes explorar cartas y metajuego sin cuenta.',
    verifyPending: 'Confirma tu correo para estrenar tus 3 días de Pro y guardar partidas.',
    verifyResend: 'Reenviar',
    verifySent: 'Enviado. Revisa tu bandeja.',
    verifiedOk: '¡Listo! Tu correo quedó confirmado y estrenas 3 días de Pro: tus partidas una a una, las estadísticas de tu mazo y el analista.',
    verifiedExpired: 'Ese enlace ya se usó o caducó. Puedes pedir otro desde tu cuenta.',
    myAccount: 'Mi cuenta',
    planLabel: 'Plan',
    planFree: 'Gratis',
    planPro: 'Pro',
    bonusDays: '{n} días de Pro por invitar',
    myInviteCode: 'Tu código para invitar',
    inviteExplain:
      'Cuando quien invitas guarda su primera partida, ganas días de Pro: 7 el primero, 3 el segundo y 1 después.',
    copy: 'Copiar',
    errEmailTaken: 'Ese correo ya tiene cuenta. Prueba a entrar.',
    errInvalidEmail: 'Ese correo no parece válido.',
    errWeakPassword: 'La contraseña necesita al menos 8 caracteres.',
    errBadCredentials: 'Correo o contraseña incorrectos.',
    errInvalidInvite: 'Ese código de invitación no existe o ya se usó.',
    errInvalidToken: 'El enlace ya se usó o caducó. Pide otro.',
    errTooMany: 'Demasiados intentos. Espera un rato y vuelve a probar.',
    errServer: 'Falló el servidor. Inténtalo de nuevo en un momento.',
    errGeneric: 'No se pudo completar. Revisa los datos e inténtalo otra vez.',
    substitutedArtHint: 'ilustración de otra impresión (la lista pide {set} {n})',
    substitutedArtNote:
      '{n} ilustración(es) son de otra impresión de la misma carta. Las energías básicas son intercambiables: para jugar y para comprar da igual cuál sea.',
  },
  en: {
    appName: 'PokeTrack',
    tagline: 'Pokémon TCG meta, decks and prices',
    search: 'Search cards',
    searchPlaceholder: 'Name, attack or effect…',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    type: 'Type',
    category: 'Category',
    rarity: 'Rarity',
    stage: 'Stage',
    set: 'Set',
    format: 'Format',
    regulationMark: 'Regulation mark',
    any: 'Any',
    standard: 'Standard',
    expanded: 'Expanded',
    results: 'results',
    noResults: 'No cards match those filters.',
    noResultsHint: 'Try removing a filter or searching for another term.',
    loading: 'Loading…',
    loadMore: 'Load more',
    hp: 'HP',
    retreat: 'Retreat',
    weakness: 'Weakness',
    resistance: 'Resistance',
    attacks: 'Attacks',
    abilities: 'Abilities',
    illustrator: 'Illustrator',
    legalIn: 'Legal in',
    notLegal: 'Out of format',
    close: 'Close',
    fallbackNotice: 'No Spanish printing: showing the English text.',
    alternateName: 'In English',
    alternateNameEs: 'In Spanish',
    prices: 'Prices',
    pricesLoading: 'Fetching prices…',
    pricesUnavailable: 'Prices unavailable right now.',
    language: 'Language',
    dbEmpty: "We're loading the card catalogue.",
    dbEmptyHint: 'It takes a few minutes. Try again in a little while.',
    apiDown: "We couldn't reach PokeTrack.",
    apiDownHint: 'It may be your connection or a brief server outage. Retry in a few seconds.',
    retry: 'Retry',
    loadFailTitle: 'We could not load this',
    loadFailHint: 'It may be your connection or a problem on our side. Your filters are still set.',
    cards: 'cards',
    zoomIn: 'Enlarge card',
    zoomOut: 'Shrink card',
    zoomHintIn: 'Click to enlarge · hold and move to tilt',
    zoomHintOut: 'Click to go back · hold and move to tilt',
    tiltHint: 'Hold and move to tilt',
    inChile: 'In Chile',
    from: 'From',
    fxNote: 'Central Bank rate',
    outOfStock: 'Out of stock',
    aggregateOffer: 'aggregate price',
    aggregateOfferHint: 'Lowest price across a marketplace’s sellers: the language and condition of that copy are unknown. It may be Japanese, reverse holo, or from a seller with a single copy — open the listing to check.',
    aggregateCheapest: 'language and condition not verified',
    englishOnlyNote: 'English printings only — what gets played at tournaments here.',
    inStock: 'In stock',
    noChilePrice: 'Not stocked at Chilean stores for this card.',
    skipToContent: 'Skip to content',
    brandTagline: 'Data for better play',
    buscadorTitulo: 'Search everything',
    buscadorPlaceholder: 'Cards, archetypes, your decks…',
    buscadorCartas: 'Cards',
    buscadorArquetipos: 'Meta archetypes',
    buscadorMisMazos: 'My decks',
    buscadorEnMeta: 'Meta archetype',
    buscadorMazoPropio: 'Your deck',
    buscadorRecientes: 'Last opened',
    buscadorAyuda: 'Type to search cards, meta archetypes and your decks.',
    buscadorAtajoMover: 'move',
    buscadorAtajoAbrir: 'open',
    buscadorAtajoCerrar: 'close',
    buscadorSinNada: 'Nothing called “{q}”.',
    buscadorSinNadaPista: 'Try fewer letters, or the name in Spanish.',
    buscadorFallo: 'We could not search. It was a connection problem, not what you typed.',
    methodologyLink: 'How it is calculated',
    navCards: 'Cards',
    navDecks: 'Decks',
    myDecks: 'My decks',
    newDeckAction: 'New deck',
    buildByHand: 'Build by hand',
    buildByHandHint: 'Search cards and put the list together',
    buildByImport: 'Import a list',
    buildByImportHint: 'Paste a list from Pokémon TCG Live or Limitless',
    backToDecks: 'My decks',
    draftDeck: 'Unsaved',
    noDecksYet: 'No decks yet',
    noDecksHint: 'Paste a list from Pokémon TCG Live, build it card by card, or take one of the decks winning tournaments right now.',
    noDecksFromMeta: 'See the meta',
    /* Signed out this screen cannot claim "no decks yet": it does not know.
       Someone arriving from another device may have twenty saved. */
    decksSignedOutTitle: 'Sign in to see your decks',
    decksSignedOutHint: 'Your decks live in your account, so you get them on any device. Want to try first? Build one now and save it when you sign in.',
    nCards: '{n} cards',
    deckTitle: 'Import list',
    deckHint: 'Paste the list exported from Pokémon TCG Live.',
    importFailed: 'We could not read the list. It is not your text: something failed while connecting. Try again.',
    copyFailed: 'The browser blocked copying. Select the list and copy it by hand.',
    deckPlaceholder: 'Pokémon: 12\n4 Spinarak ME3 1\n…',
    import: 'Import',
    importing: 'Importing…',
    deckValid: 'Valid list',
    deckInvalid: 'The list has problems',
    saveImported: 'Save deck',
    savingDeck: 'Saving…',
    deckNamePlaceholder: 'Deck name (optional)',
    needsSessionDeck: 'Sign in to save the deck.',
    importedDeckName: 'Imported deck',
    deckSaved: 'Saved as "{nombre}". You can now link matches to it.',
    deckSaveFailed: 'Could not save the deck.',
    saveOnlyResolved: '{n} of {total} lines will be saved: the rest could not be identified.',
    copyList: 'Copy list',
    copied: 'Copied',
    clearDeck: 'Clear',
    unresolvedLines: 'Unresolved lines',
    errDeckSize: 'The list has {n} cards and must have 60.',
    errCopyLimit: 'There are {n} copies of {name}; the maximum is 4.',
    errNotLegal: '{name} is not legal in {format}.',
    errUnresolved: 'Not recognised: {line}',
    didYouMean: 'Did you mean…?',
    errNoPokemon: 'The list has no Pokémon.',
    errNoBasic: 'No Basic Pokémon. You cannot start a game with this list.',
    errAceSpec: 'There are {n} ACE SPEC cards ({names}); only 1 per deck is allowed.',
    warnAmbiguous: '{name} exists in several sets; {chosen} was chosen.',
    totalCards: 'cards total',
    builderSearch: 'Search cards to add…',
    removeCopy: 'Remove one copy',
    addCard: 'Add to deck',
    yourDeck: 'Your deck',
    builderEmpty: 'Search for cards above and click one to add it.',
    exportDeck: 'Export to game',
    vsMetaTitle: 'Your deck vs the meta',
    vsMetaLists: 'across {n} meta lists',
    vsMetaCore: 'of the core',
    vsMetaShare: 'Share',
    vsMetaWr: 'Win rate',
    vsMetaMissing: "Core cards you're not running",
    vsMetaUnusual: 'Almost nobody plays these',
    vsMetaOthers: 'Also close to',
    vsMetaHint:
      "Similarity is measured against the archetype's core only: cards played by at least 60% of lists. Counting every card would score high for anything, since any two Standard decks share Trainers and basic energy. Running something unusual isn't a mistake; sometimes it's exactly why a deck wins.",
    analistaPedir: 'Request analysis',
    analistaRepetir: 'Re-run analysis',
    analistaPensando: 'Reading the deck and the meta…',
    analistaEspera:
      'The analyst reviews the last 14 days of tournaments: it can take up to half a minute.',
    analistaBloqueado:
      "Wait for the analyst: if the list changes mid-analysis, the result would no longer describe your deck.",
    analistaNecesita60: 'Analysis needs the full list: 60 cards.',
    analistaConfianza: 'Confidence',
    analistaConf_alta: 'high',
    analistaConf_media: 'medium',
    analistaConf_baja: 'low',
    analistaCached: 'analysis current for today’s meta',
    analistaComo: 'Read as',
    analistaEsperado: 'expected win rate today',
    analistaC_consistencia: 'Consistency',
    analistaC_estructura: 'Structure',
    analistaC_meta: 'Meta position',
    analistaC_estrategia: 'Strategy',
    analistaC_cartas: 'Card choices',
    analistaC_resiliencia: 'Resilience',
    analistaSinMatriz:
      "Deck outside the recorded meta: the position score is the analyst's reading, not tournament data, and confidence drops.",
    analistaFortalezas: 'Strengths',
    analistaDebilidades: 'Weaknesses',
    analistaSugerencias: 'Suggested changes',
    analistaAplicar: 'Apply change',
    analistaAplicarTodo: 'Apply suggestions',
    analistaAplicada: 'Applied',
    analistaDeshacer: 'Undo',
    analistaTope:
      'The 4-copy limit per name trimmed the suggestion ({cards}): the deck stays legal.',
    analistaAplicarParcial:
      'Only part of this change was recognized as a card; what was recognized gets applied.',
    analistaAplicadoNota:
      'Changes applied and saved to the list. The score refers to the previous list: re-run the analysis when you finish.',
    analistaMatchups: 'On the matchups',
    analistaError: "The analyst couldn't answer. Try again in a moment.",
    analistaLimite: "This hour's analyses are used up; come back in a bit.",
    analistaHint:
      "Every number quoted comes from real tournaments in the last 14 days; the analyst never estimates figures. The score combines computed metrics (consistency, structure, meta position) with the analyst's judgment (strategy, cards, resilience). That judgment is an AI-generated opinion over partial data — small samples in some matchups, a rotating meta and no context from your actual games — and it can be wrong: treat it as a second opinion, not as truth.",
    analistaSugerenciasAviso:
      'Ideas to test, not guaranteed upgrades: nothing forces you to apply them and the final call on your deck is yours.',
    exportedList: 'List for Pokémon TCG Live',
    missingSetCode: 'No known set code (check these lines in the game)',
    navMeta: 'Meta',
    metaTitle: 'Pokémon TCG Meta',
    metaTagline: 'The Standard tier list built from real tournament data — the same meta you play in Pokémon TCG Live.',
    metaTournaments: 'tournaments',
    metaPlayers: 'players',
    metaGames: 'games',
    deckNoGames: 'No games yet',
    deckTrackerOn: 'In the tracker',
    deckTrackerUse: 'Use in the tracker',
    privacyLink: 'Privacy & data',
    desktopAppLink: 'Desktop app (Windows) · beta',
    desktopAppNote: 'Live tracker for PTCG Live, in beta: it improves day by day and may fail on setups not yet tested. Requires the game at 16:9 — 1920×1080 fullscreen recommended, the tested setup.',
    signUpAccept: 'By creating your account you accept the {link}.',
    signUpAcceptLink: 'privacy & data policy',
    pgLabel: 'Ranking pages',
    pgPrev: 'Previous page',
    pgNext: 'Next page',
    metaLive: 'LIVE',
    donutGlobal: 'overall',
    reprocessMatch: 'Re-analyze',
    reprocessHint: 'Reads the log again with the current parser and fixes anything that was misread.',
    reprocessedOk: 'Match re-analyzed with the current parser.',
    // --- Community decks ---
    energiaGrupo: 'Energy',
    decksMine: 'My decks',
    decksCommunity: 'Community',
    communityTitle: 'Community decks',
    communityHint: 'Shared by other players: copy them, try them, and vote the ones that beat you.',
    communityEmptyTitle: 'The shelf is empty',
    communityEmpty: 'Be the first: open one of your decks and hit Share.',
    communityBy: 'by {name}',
    communityAnon: 'an anonymous trainer',
    communityTop: 'Top',
    communityNew: 'New',
    communityLike: 'Like',
    voteNeedsAccount: 'Sign in with a verified email to vote.',
    copyToMine: 'Copy to my decks',
    copiedToMine: 'Copied to your decks.',
    shareDeck: 'Share',
    unshareDeck: 'Stop sharing',
    sharedBadge: 'Shared',
    sharedOkAviso: 'Shared with the community. Let them try it!',
    unsharedAviso: 'The deck is no longer shared.',
    reshareHint: 'Already shared: sharing again publishes the current list.',
    shareDuplicate: 'An identical deck is already shared: “{name}” by {author}. Change a card to publish yours.',

    /* --- Public deck catalogue and shared detail ------------------------ */
    publicDecksTitle: 'Public decks',
    publicDecksHint:
      'Lists played at tournaments and decks people share, in one table: what each one runs, what it costs and how it did.',
    publicSearchPlaceholder: 'Search by deck, player or tournament…',
    publicSource: 'Source',
    publicAll: 'All',
    publicTournament: 'Tournament',
    publicCommunity: 'Community',
    anyArchetype: 'Any archetype',
    publicBestPerArchetype: 'Best list per archetype only',
    publicCount: '{n} decks',
    publicNoMatchTitle: 'No deck matches that',
    publicNoMatch: 'Try fewer filters or a different term.',
    publicDeckCol: 'Deck',
    publicAuthor: 'Creator',
    publicCostCredits: 'Credits',
    publicCostClp: 'Price',
    publicGames: 'Games',
    publicCountry: 'Country',
    publicViewShort: 'View',
    draftHint: 'Unsaved — it lives in this browser',
    draftDiscard: 'Discard',
    deckEmptyNoSave: 'An empty deck is not saved: add a card first.',
    publicRounds: 'Rounds',
    publicRoundsHint: 'Rounds this player played at that tournament. The ceiling is the tournament round count (14 at the longest), not the archetype total.',
    publicMetaShare: 'Share',
    publicShared: 'Shared by a player',
    publicUnknownRarity: '{n} cards with no known rate: the total falls short.',
    publicMissingPrices: '{n} cards with no local price: the total falls short.',
    publicAggregatePrices: '{n} cards priced from an aggregate, with no verified language.',
    publicLowSample: 'few',
    publicLowSampleHint: 'Only {n} games: the percentage does not say much yet.',
    publicCore: 'Core',
    publicCopyShort: 'Copy',
    edit: 'Edit',
    exportShort: 'Export',
    addCards: 'Add cards',
    pasteDeckHere: 'Paste the list copied from the game here…',
    publicLoadFailed: 'Could not load the decks.',
    publicBack: 'Back to the list',
    publicYours: 'One of your decks',
    publicList: 'The list',
    publicNumbers: 'The numbers',
    publicRecordRow: 'Record',
    publicArchWinRate: 'Archetype win rate',
    publicNGames: '{n} games',
    publicLooksLike: 'It looks {n}% like {name}.',
    publicComposition: 'What it is made of',
    publicOddsPro: 'Opening odds are Pro, here and on your own decks.',
    prizeLoad: 'Prize load',
    prizeWorst: 'one knockout costs up to {n}',
    prizeOne: '1 prize',
    prizeTwo: '2 prizes',
    prizeThree: '3 prizes',
    catPokemon: 'Pokémon',
    catTrainer: 'Trainer',
    catEnergy: 'Energy',
    catSupporter: 'Supporter',
    catItem: 'Item',
    catTool: 'Tool',
    catStadium: 'Stadium',
    metaDelayed: 'META FROM {n} DAYS AGO',
    metaDelayedHint:
      'The free plan sees the metagame one week behind. With Pro you see it up to date, as tournaments come in.',
    metaUpdatedAgo: 'updated {t} ago',
    haceMin: '{n} min',
    haceHoras: '{n} h',
    metaWindow: 'Window',
    rank: '#',
    allFormats: 'All formats',
    edge: 'Underplayed',
    edgeHint:
      'Win rate above 50% minus share: high means it wins more than it gets played.',
    sortBy: 'Sort by',
    sortAsc: 'ascending',
    sortDesc: 'descending',
    conversion: 'Top 8',
    conversionHint:
      'Share of entries with that deck that finished in the top 8. It also counts players who dropped: they entered with that deck. Narrow the tournament size to make the number comparable.',
    minTournament: 'Tournament size',
    anySize: 'Any',
    playersN: '{n}+ players',
    minSample: 'Minimum sample',
    minSampleAny: 'No minimum',
    gamesN: '{n}+ games',
    archetypesShown: '{n} of {total} archetypes',
    days7: 'Last week',
    days30: 'Last 30 days',
    days90: 'Last 90 days',
    daysAll: 'All',
    archetype: 'Archetype',
    archDetailToggle: 'Show details for {name}',
    share: 'Share',
    winRateShort: 'Win rate',
    games: 'games',
    coreCards: 'Archetype cards',
    lists: 'lists',
    matchups: 'Matchups',
    minGames: 'min per cell:',
    matchupLegend:
      "Each cell is the row's win rate against the column. A dot means not enough games.",
    metaEmpty: 'No metagame data.',
    metaEmptyHint: 'Run the ingest with: npm run ingest:meta -w server',
    metaNoData: 'Not enough games in this window.',
    data: 'Data',
    dataStatus: 'Data status',
    autoNote:
      'These refresh on their own: cards weekly, metagame daily. Prices and exchange rate are fetched live.',
    updateNow: 'Update',
    updating: 'Updating…',
    never: 'never',
    justNow: 'less than 1 h ago',
    daysAgo: 'days ago',
    lastRunFailed: 'last run failed',
    paneArchetypes: 'Archetypes',
    paneMatchups: 'Full matrix',
    paneMatchupsShort: 'Matrix',
    paneUpcoming: 'Upcoming',
    tourPasados: 'Played',
    upcomingRegion: 'Region',
    upcomingLatam: 'Latin America',
    upcomingChile: 'Chile',
    upcomingOnline: 'Online',
    upcomingTodos: 'All',
    upcomingSoloAbiertos: 'Registration open',
    upcomingOficial: 'Official',
    upcomingComunidad: 'Community',
    upcomingApuntarse: 'Register',
    upcomingVerEvento: 'View event',
    upcomingInscritos: '{n} registered',
    upcomingVacio: 'Nothing here yet',
    upcomingVacioHint: 'No tournaments announced with these filters. Try viewing all of them.',
    upcomingFallo: 'Could not load the tournament calendar.',
    upcomingFuente: 'Calendar from limitlesstcg.com and play.limitlesstcg.com. Registration happens on their site.',
    paneTournaments: 'Tournaments',
    filterTrainerType: 'Trainer',
    filterMark: 'Reg. mark',
    subAllCards: 'All cards',
    subSets: 'Sets',
    setNCards: '{n} cards',
    setPlayedPct: '{n} % sees tournament play',
    metaTrend: 'Trend',
    metaTrendWeeks: 'last {n} weeks',
    perfilTournaments: 'tournaments',
    perfilRecord: 'Record',
    perfilTop8: 'top 8',
    perfilFavDeck: 'Usual deck',
    perfilRecent: 'Recent results',
    perfilPlaceOf: '#{p} of {n}',
    /* When the badge already shows the placing, the text only states the field
       size: otherwise it reads "1 #1 of 137" with the one repeated. */
    perfilOutOf: 'of {n}',
    panePlayers: 'Players',
    playersTournaments: 'Tournaments',
    playersFirsts: '1st',
    playersHint: 'Ranked by top 8s, titles and wins in the chosen period.',
    searchPlayer: 'Search player…',
    playersShown: '{n} of {total} players',
    playersSearchEmpty: 'No player matches “{q}”.',
    tourStandingsTab: 'Standings',
    tourRoundsTab: 'Rounds',
    roundShort: 'R{n}',
    topRoundShort: 'Top · R{n}',
    tourTie: 'Tie',
    tourBye: 'Bye',
    filterAbility: 'Ability',
    abilityYes: 'With ability',
    filterArtist: 'Artist',
    addMatch: 'Log a game',
    historyTitle: 'Match history',
    moreFilters: 'More filters',
    moreActions: 'More',
    collapseForm: 'Collapse the game form',
    otherElement: 'Other',
    onlyThisElement: 'See this element’s decks',
    showAllElements: 'Close',
    seeMyDeck: 'See my deck',
    seeMyDeckHint: 'See the list of the deck you played this match with',
    autoTag: 'auto',
    autoTagHint: 'Auto-labeled from the game evidence (high confidence). Any manual change makes it yours.',
    tourPlayers: '{n} players',
    tourLists: '{n} decklists',
    tourWinner: 'Winner',
    tourPlayer: 'Player',
    tourRecord: 'Record',
    tourDeck: 'Deck',
    tourViewList: 'View list',
    tourTop: 'Top {n} of {total} players',
    tourNoLists: 'This tournament did not publish decklists.',
    bestMatchups: 'Favorable matchups',
    worstMatchups: 'Uphill matchups',
    searchArchetype: 'Search archetype…',
    archSearchEmpty: 'No archetype matches “{q}”.',
    localMarketRef: 'local market reference',
    deckStatsProTitle: 'Deck statistics are a Pro feature',
    deckStatsProBody: 'Mulligan, opening hand, prizes and the comparison against the meta.',
    gateDeckStatsTitle: 'Deck analysis, with Pro',
    gateDeckStatsBody:
      'The legality check is free. Pro opens the probabilities — mulligan, opening hand, being prized — and how your list compares against the meta.',
    proDeckStats: 'Your deck’s statistics: opening, prizes and versus the meta',
    gateAnalistaTitle: 'The analyst, with Pro',
    gateAnalistaBody:
      'An AI evaluation of your deck grounded in real tournaments: a rubric-based score, strengths, weaknesses and suggested changes with evidence. Every analysis draws on the last 14 days of data.',
    proAnalista: 'The analyst: your deck evaluated with AI and tournament data',
    expectedVsField: 'Expected win rate against the current meta:',
    fieldCovered: 'covers',
    deckCheck: 'Deck check',
    chkMissing: '{n} cards short of 60.',
    chkOver: '{n} cards over: a deck must be exactly 60.',
    chkNoBasics: 'No Basic Pokémon. You cannot start a game with this list.',
    chkMaxCopies: 'You run {n} copies of {card}. The limit is 4.',
    chkAceSpec: 'You run {n} ACE SPEC cards ({cards}). Only 1 per deck is allowed.',
    chkRotation: 'Next rotation this deck loses {n} cards (mark {marca}): {cards}.',
    dockSeeDeck: 'View deck',
    dockSeeSearch: 'Find cards',
    deckCostTitle: 'Deck cost',
    costListTitle: 'Shopping list',
    costNoPrice: 'no local price',
    handSimTitle: 'Opening hand',
    handDraw: 'Draw 7 cards',
    handRedraw: 'Draw another hand',
    handMulligan: 'Mulligan: no Basic Pokémon. On the table you reveal, shuffle and draw again.',
    communityCredits: 'credits (craft)',
    communityBuyCost: 'buy (approx.)',
    pasteFromClipboard: 'Paste from clipboard',
    clipboardEmpty: 'The clipboard is empty.',
    clipboardDenied: 'The browser blocked clipboard access: paste with Ctrl+V (or long-press and “Paste”).',
    tagOther: 'Other (type it)…',
    tagOtherPlaceholder: 'Opposing deck name',
    addToDeck: 'Add to deck',
    addedToDeck: '{card} added to the deck.',
    pasteFromGame: 'Import deck',
    pastedDeck: 'List pasted: {n} cards in the deck.',
    pasteNoDeck: "The clipboard doesn't hold a game list. Copy your deck in PTCG Live and try again.",
    confirmPasteDeck: 'Replace the deck with the pasted list?',
    confirmPasteDeckDetail: 'The current {n} cards will be replaced by the clipboard list.',
    pasteAction: 'Replace',
    pasteUnresolved: "{n} lines couldn't be matched and were left out.",
    addedToDraft: 'Added to the draft ({n} cards). Find it in Decks as “Unsaved”.',
    addLimit: 'You already run 4 copies of {card}.',
    addFailed: 'The card could not be added.',
    trendTitle: 'Price trend',
    trendClp: 'cheapest in Chile · CLP',
    trendUp: 'Up {p} %',
    trendDown: 'Down {p} %',
    trendFlat: 'Steady',
    trendKeys: 'Left and right arrows step through the days.',
    pricesIntl: 'International reference',
    metaHowHint: 'Standard tournaments from Limitless with 32+ players, every 24 h. Tap to see how each number is calculated.',
    metaGoTournaments: 'See played tournaments',
    metaGoPlayers: 'See the player ranking',
    metaGoMatchups: 'See the matchup matrix',
    viewDeckPhoto: 'View photo',
    chkMulligan: 'With {basics} Basics, 1 in {n} hands starts on a mulligan. 90% of tournament lists run {referencia} or more.',
    oddsAfter60: 'Odds appear once the deck is complete. {n} cards to go.',
    oneEvery: '1 in {n} hands',
    ofSixty: 'of 60 cards',
    card: 'Card',
    deckOdds: 'Opening odds',
    mulliganRate: 'Mulligan',
    basicsCount: 'Basics',
    openingHand: 'Opening hand',
    prizedOdds: 'Prized',
    oddsColMano: 'Hand',
    oddsColPremio: 'Prized',
    oddsHint:
      'Computed exactly, not simulated. "Prized" is the chance that at least one copy ends up among the 6 prize cards.',
    oddsIncomplete: 'The list is not 60 cards yet: these numbers are indicative.',
    mulliganHigh: 'Few Basics for this deck size.',
    navMatches: 'My matches',
    navMatchesShort: 'Matches',
    pasteLog: 'Paste battle log',
    modeLog: 'Pokémon TCG Live',
    modeManual: 'Manual',
    recordOrigin: 'Source',
    manualTitle: 'Log a physical match',
    manualHint: "For tabletop games: who won, each player's deck and, if you remember, the prizes each side took.",
    iWon: 'I won',
    iLost: 'I lost',
    prizesMine: 'Prizes I took',
    prizesTheirs: "Opponent's prizes",
    opponentOptional: "Opponent's name (optional)",
    manualNeedsResult: 'Pick who won to be able to save.',
    manualSaveFailed: "Couldn't save the match.",
    manualMatchDetail: 'Physical match logged by hand: there is no turn-by-turn record.',
    pasteLogHint:
      'In Pokémon TCG Live, when the match ends tap BATTLE LOG on the victory/defeat screen, then the export icon. It goes to your clipboard and is lost when you leave to the menu, so paste it before exiting.',
    saveMatch: 'Save match',
    saving: 'Saving…',
    whichAreYou: 'Which one are you? Asked only once.',
    playingAs: 'Playing as',
    change: 'change',
    aliasFilter: 'PTCG Live alias',
    aliasAll: 'All',
    profileLabel: 'Game profile',
    profileNone: 'No profile',
    matchSaved: 'Match saved.',
    matchDuplicate: 'That match was already saved.',
    matchLogHeader: 'Your log starts with: “{linea}”. If the game is in a language or region variant that is not covered yet, send us that line and we will add it.',
    matchNotALog: "That doesn't look like a Pokémon TCG Live battle log.",
    record: 'Record',
    goingFirst: 'Going first',
    opponent: 'Opponent',
    myDeckCol: 'My deck',
    noDeck: 'No deck',
    acceptDeckSuggestionHint: 'Deduced from your Pokemon in the match. Click to apply.',
    byDeck: 'By deck',
    winsOfGames: '{wins} of {games}',
    unlinkedMatches: '{n} matches with no deck linked: they are not counted here.',
    unlinkedMatchesOne: '1 match with no deck linked: it is not counted here.',
    theirDeck: 'Their deck',
    result: 'Result',
    prizes: 'Prizes',
    turnsShort: 'Turns',
    dateCol: 'Date',
    numCol: 'No.',
    numColFull: 'Row number',
    wentFirstCol: 'First turn',
    wentFirstShort: '1st turn',
    endViaCol: 'Finish',
    endPrizes: 'Prizes',
    endConcession: 'Concession',
    endBench: 'No bench',
    wentFirstYes: 'Yours',
    wentFirstNo: 'Theirs',
    datePlayed: 'Date played',
    editDate: 'Change the date',
    calPrevMonth: 'Previous month',
    calNextMonth: 'Next month',
    calToday: 'Today',
    copyLog: 'Copy log',
    exportCopied: 'List copied: paste it in Pokémon TCG Live when importing a deck.',
    exportBelow: 'List generated below, with its copy button.',
    notifBell: 'Notifications',
    notifBellUnread: 'Notifications: {n} unread',
    notifTitle: 'Notifications',
    notifEmpty: 'Nothing new here.',
    notifVoto: 'Someone liked your deck “{mazo}”.',
    notifMeta: 'The meta was updated: {n} tournaments in the last week.',
    notifNuevoUsuario: 'New account: {quien}.',
    notifMazoCompartido: '{autor} shared “{mazo}” with the community.',
    notifBuzonBug: 'New bug in the suggestions inbox.',
    notifBuzonIdea: 'New suggestion in the inbox.',
    notifSuscripcion: 'Subscription from {correo}: {estado}.',
    notifClear: 'Clear',
    notifAhora: 'just now',
    notifHaceMin: '{n} min ago',
    notifHaceHoras: '{n} h ago',
    notifHaceDias: '{n} days ago',
    win: 'Win',
    loss: 'Loss',
    tie: 'Tie',
    iTied: 'We tied',
    first: 'first',
    second: 'second',
    noMatches: 'No matches logged yet',
    noMatchesHint: 'Paste a Pokémon TCG Live match log and it reads itself, or note a paper match by hand.',
    // Official deck registration sheet for leagues and tournaments (HojaRegistro.jsx).
    regButton: 'Tournament sheet',
    regTitle: 'Tournament deck registration',
    regIntroDeck: 'The official Play! Pokémon sheet with "{name}", ready to print or save as PDF.',
    regIntroPaste: 'Paste or type your list — as Pokémon TCG Live exports it, or by hand: "4 Abra TWM 80" — and get the official sheet ready to print or save as PDF.',
    regPasteLabel: 'Your list',
    regGenerate: 'Build sheet',
    regRegenerate: 'Update sheet',
    regPlayer: 'Your details',
    regName: 'Full name',
    regBirth: 'Date of birth',
    regDivision: '{div} Division in the {season} season.',
    regDivisionNone: 'Your date of birth ticks the division for you.',
    regDiv_junior: 'Junior',
    regDiv_senior: 'Senior',
    regDiv_masters: 'Masters',
    regFormat: 'Format',
    regPrivate: 'Your details stay in this browser: they are not sent or saved with the deck.',
    regNot60: 'The list adds up to {n} cards, not 60.',
    regUnresolved: '{n} line(s) don’t match the catalog and print as you wrote them:',
    regRenamed: '{n} card(s) print under their English name; check it’s the one you meant:',
    regIgnored: '{n} line(s) couldn’t be read and were left out:',
    regPrint: 'Print or save PDF',
    regPdfHint: 'For a PDF, pick "Save as PDF" as the printer.',
    regPreviewEmpty: 'Your sheet will show up here once you build your list.',
    regFailed: 'Couldn’t prepare the sheet. Please try again.',
    viewDeckSheet: 'View full deck',
    deckSheetHint: 'Whole cards with counts, to photograph or take shopping.',
    downloadPng: 'Download PNG',
    generating: 'Generating…',
    print: 'Print',
    pngFailed: 'Could not generate the image. You can screenshot or print instead.',
    // Deck sheet: it is ONE real list, not the archetype average. Saying so
    // turns what reads as a contradiction into the actually useful signal.
    sheetIsOneList: "{player}'s actual list",
    sheetIsOneListPlain: 'One real list, not the archetype average',
    sheetPlacing: 'placed {n}',
    sheetPickList: "The archetype's real lists — pick which to view",
    inclusionOf: 'in {pct} of lists',
    techPick: 'Personal pick',
    techPickHint:
      'Fewer than a quarter of lists run it: this is the player, not the archetype.',
    coreMissing: 'Core cards this list skips',
    coreMissingHint:
      'Cards most of the archetype plays that this list leaves out. Usually the most revealing part.',
    openCardHint: 'Click a card to open its page.',
    // --- Plans ---
    gateDecksTitle: 'Save as many decks as you want',
    gateDecksBody:
      'The free plan saves up to {limit} decks. With Pro you save as many as you like and get them on any device.',
    gateMatrixTitle: 'The full matrix is Pro',
    gateMatrixBody:
      'Cross every archetype against every other to prepare for a tournament. Each deck’s favorable and uphill matchups stay free.',
    gateCommunityTitle: 'Share your decks with Pro',
    gateCommunityBody:
      'Publishing your decks, signing them with your name and climbing the community ranking is a Pro feature. Viewing, copying and voting shared decks is free.',
    gateMetaTitle: 'Full metagame with Pro',
    gateMetaBody:
      'The free plan shows the 3 most played archetypes, one week behind. With Pro you see the whole field, up to date and for any period.',
    gateMatchesTitle: 'Every game, with Pro',
    gateMatchesBody:
      'You save games and see your deck summary for free. Pro opens the full list, the turn-by-turn detail of each game, and your record against every opponent.',
    gateGenericTitle: 'This is a Pro feature',
    gateUsage: 'You have {current} of {limit}.',
    proDecks: 'Unlimited decks, synced across devices',
    proMeta: 'Export meta decks in the format Pokémon TCG Live imports',
    proHistory: 'Unlimited match history',
    proTracker: 'Live match tracking (in progress)',
    proSoon: 'Payments are not live yet. Write to us if you want early access.',
    goPro: 'Go Pro for ${price}/mo',
    tagDeck: 'Tag',
    untagged: 'Untagged',
    myMatchups: 'How you do against each deck',
    myMatchupsHint: "Tag the opponent's deck in your history and you'll see your record next to what that archetype does in the meta.",
    filterByDeck: 'Narrow by deck',
    deckLabel: 'Deck',
    decksNoGames: 'No games yet:',
    gateSignUp: 'Create your account — 3 days of Pro when you confirm your email',
    seeAllProTrial: 'See it all — 3 days free',
    matchDeleted: 'Game deleted.',
    deckDeleted: 'Deck deleted.',
    savedToMyDecks: 'Saved to your decks.',
    matrixHowTo: 'Each cell: win % of the ROW deck against the column deck. The mirror cell counts the same games from the other side, so 60% here is ~40% there (ties keep it from summing to exactly 100).',
    matrixCorner: 'Rows: your deck. Columns: the opponent.',
    matrixCellTip: '{a} wins {pct}% of {n} games against {b}',
    matrixPairTip: '{a} {pa}% · {b} {pb}% — same {n} games',
    matrixNoSample: '{a} vs {b}: only {n} games, not enough sample',
    mxPickDeck: 'My deck',
    mxWorstFirst: 'Worst first',
    mxBestFirst: 'Best first',
    mxVeryBad: 'Very unfavourable',
    mxBad: 'Unfavourable',
    mxEven: 'Even',
    mxGood: 'Favourable',
    mxVeryGood: 'Very favourable',
    mxNoData: 'Not enough data',
    mxRowAria: '{a} vs {b}: {p}% over {n} games, {v}',
    mxRowAriaNoData: '{a} vs {b}: not enough data, only {n} games',
    logTruncated: 'The log arrived cut off: it does not include the end of the game, so the result cannot be deduced. This usually happens when copying before the game writes the closing lines.',
    editDeck: 'Edit deck',
    doneEditing: 'Done editing',
    fnModalTitle: 'Everything PokeTrack does',
    seeAllFeatures: 'see everything it does →',
    fnModalSub: 'Up-to-date meta, decks, and full analysis of your Pokémon TCG Live games.',
    fnCardsTitle: 'Complete card library',
    fnCardsBody: 'Over 4,700 cards in Spanish and English, searchable by name, attack or effect, filters by type and format, every print of each card, and prices from Cardmarket, TCGplayer and Chilean stores.',
    fnMetaTitle: 'Meta rankings, always current',
    fnMetaBody: 'The archetypes dominating competitive play, with field share, win rates and matchups, computed from real tournaments and updated daily. Filterable by format and period.',
    fnDecksTitle: 'Deck builder and analysis',
    fnDecksBody: "Build decks by hand or import them from the game, with opening-hand odds, mulligan rate, statistical suggestions and a comparison against the meta. Export any deck — yours or the meta's — in the format Pokémon TCG Live imports.",
    fnMatchesTitle: 'Pokémon TCG Live game tracker',
    fnMatchesBody: 'Paste the battle log and the game is saved: result, prizes, turns and the turn-by-turn story. Per-deck stats — win rate, going first, average turns, prize differential — and your record against every rival archetype.',
    fnRivalTitle: 'Rival deck identification',
    fnRivalBody: "From the Pokémon seen on your opponent's side, PokeTrack deduces which archetype they played and how certain it is, trained on thousands of tournament lists. Works with the game in Spanish or English.",
    fnProTitle: 'And with Pro, no limits',
    fnProBody: 'Unlimited decks, deck statistics (opening, mulligan, prizes and versus the meta), the analyst — your deck evaluated with AI and real tournament data: score, weaknesses and suggested changes —, complete history with turn-by-turn detail, and meta-to-game export. Every new account gets 3 days of Pro on confirming their email.',
    exportToGame: 'Export to game',
    copyManually: 'Clipboard was blocked: select and copy by hand.',
    savedToDecks: 'Saved ✓',
    saveToMyDecks: 'Save to my decks',
    exportFailed: 'Export failed. Try again.',
    gateMetaExportTitle: 'Take the meta into the game',
    gateMetaExportBody: 'With Pro you export any meta deck in the format Pokémon TCG Live imports, or save it to your decks to track your games with it.',
    otherPrints: 'Other prints ({n})',
    evolutionLine: 'Evolution line',
    prevCard: 'Previous card',
    nextCard: 'Next card',
    newVersionReady: 'A new version of PokeTrack is ready.',
    reloadNow: 'Reload',
    battleStart: 'Battle begins against {name}',
    battleEndWin: 'Battle over: victory!',
    battleEndLoss: 'Battle over: {name} wins',
    battleEndUnknown: 'Battle over: no recognized result',
    seeArchetype: 'see their deck',
    seeArchetypeHint: 'Opens this archetype in the Meta, with its aggregated field list.',
    avgTurns: 'Avg. turns',
    prizeDiff: 'Prize diff',
    playedWith: 'Played with',
    autoDetected: 'detected ({pct}%)',
    analyzingLog: 'Analyzing the log…',
    analyzing: 'Analyzing…',
    onlyDeckMatches: 'Only the {n} games with "{name}".',
    showAll: 'Show all',
    deckMatchesLink: 'View games',
    changeTag: 'Change this tag',
    tagNotSaved: 'The change could not be saved. It was left as it was.',
    tagChanged: 'Tag changed.',
    undoTo: 'Back to "{name}"',
    mismatchSays: 'what was seen points to {name} ({pct}%)',
    mismatchHint: "The Pokémon seen on the opponent's side don't match this tag. Click to correct it.",
    deckMismatchHint: 'The Pokémon you played do not match this deck. Click to correct it.',
    unknownResult: 'No result',
    wentFirst: 'You went first',
    wentSecond: 'Opponent went first',
    nTurns: '{n} turns',
    nTurnOne: '1 turn',
    turnN: 'Turn {n}',
    yourTurn: 'yours',
    theirTurn: "opponent's",
    setupPhase: 'Setup',
    matchDetail: 'Match detail',
    vsOpponent: 'Against {name}',
    detailFailed: 'Could not load the detail.',
    noLogStored: 'This match was saved before logs were stored, so there is no turn-by-turn.',
    logUnparsed: 'The log is stored but could not be read.',
    nothingLogged: 'Only draws, energy and trainers.',
    fullLog: 'View the full log',
    evAttack: '{pokemon} uses {move} on {target} · {damage} damage',
    evKo: '{card} is knocked out',
    evPrize: 'takes {n} prizes',
    evPrizeOne: 'takes 1 prize',
    evEvolve: 'evolves into {card}',
    evAbility: '{pokemon} uses {name}',
    evPlay: 'plays {card}',
    evConcede: 'concedes',
    gameOne: 'game',
    allDecks: 'All',
    lowSampleHint: 'Few games: this percentage swings a lot with each result.',
    noMatchupsForDeck: 'No games with this deck against a tagged opponent yet.',
    metaLabel: 'in the meta:',
    ofField: 'of the field',
    notInMeta: 'no meta data',
    madeBy: 'Made by',
    footNote: 'Tournament data via Limitless, card data via TCGdex. Unofficial project: not produced or endorsed by Nintendo or The Pokémon Company.',
    savedDecks: 'My decks',
    deckName: 'Deck name',
    saveDeck: 'Save',
    savedOk: 'Saved',
    unsavedChanges: 'Unsaved changes',
    newDeck: 'New',
    deleting: 'Deleting…',
    deleteFailed: 'Could not delete the deck. Try again.',
    deleteDeck: 'Delete',
    signInToSave: 'Sign in to save your decks and get them on any device.',
    showingOf: 'Showing {n} of {total}',
    seeAllPro: 'See all with Pro',
    matchListProTitle: 'See every game with Pro',
    matchListProBody: 'You have {n} saved. Pro opens the list, the turn-by-turn detail and your record against every opponent.',
    adminTitle: 'Administration',
    adminUsers: 'Users',
    adminVerified: 'verified',
    adminDecks: 'Decks',
    adminMatches: 'Matches',
    adminSearch: 'Search by email or name…',
    adminGrant: 'Grant Pro',
    adminRevoke: 'Revoke Pro',
    adminRevokeTitle: 'Revoke Pro?',
    leaveUnsavedTitle: 'Leave without saving?',
    leaveUnsavedDetail: 'The changes to this deck will be lost.',
    leaveUnsavedAction: 'Leave without saving',
    adminRevokeDetail: '{who} will move to the free plan and lose Pro features. You can grant it again anytime.',
    adminPlanFailed: "Couldn't change the plan. Try again.",
    adminUnverified: 'unverified',
    adminNoUsers: 'No user matches.',
    adminAnalytics: 'My visits in analytics',
    adminAnalyticsIn: "Your visits count in the metrics like any other user's.",
    adminAnalyticsOut: "This account is excluded: your visits don't count in the metrics, wherever you sign in from.",
    adminAnalyticsExclude: 'Exclude me',
    adminAnalyticsInclude: 'Count me again',
    tallerTitle: 'Deck workshop',
    tallerSub: "against today's meta",
    imgCacheTitle: 'Image cache',
    imgCacheSub: 'of ~{objetivo} · {mb} MB',
    imgCacheVacia: 'nothing stored yet',
    imgCacheFallo: 'cannot write to disk',
    tallerOpen: 'Open workshop',
    tallerBack: 'Back to panel',
    tallerMeta: 'Meta snapshot',
    tallerTournaments: 'tournaments',
    tallerPlayers: 'players',
    tallerWindow: 'window',
    tallerLastIngest: 'last ingest',
    tallerMyDecks: 'My decks',
    tallerHold: 'Hold',
    tallerTune: 'Tune',
    tallerRebuild: 'Rebuild',
    tallerArchetype: 'Archetype',
    tallerNoArchetype: 'Outside the recorded meta',
    tallerExpectedWr: 'Expected WR',
    tallerWorst: 'Worst matchups',
    tallerMissing: 'Core cards missing',
    tallerUnusual: 'Off-template',
    tallerPrizesPerKo: 'prizes per knockout',
    tallerMegaWarn: 'Runs Mega Evolution ex: every knockout is 3 prizes.',
    tallerOpportunities: 'Hidden gems',
    tallerOpportunitiesHelp: "Archetypes that win and almost nobody plays. This is where a deck your opponent won't expect comes from.",
    tallerNoOpportunities: 'Nothing passes the filter today.',
    tallerGuide: 'Table guide',
    tallerGuideEmpty: 'No guide yet. Write the plan, the lethal math and how this deck wins.',
    tallerGuideAdd: 'Write it myself',
    tallerGuideEdit: 'Edit',
    tallerGuideWrite: '✦ Write the guide',
    tallerGuideRewrite: '✦ Rewrite guide',
    tallerGuideWriting: 'Writing…',
    tallerGuideOverwrite: 'There is already a guide. Replace it?',
    tallerGuideOverwriteOk: 'Yes, rewrite it',
    tallerGuideLimit: 'You hit the request limit for now. Try again in a while.',
    tallerGuideFailed: "The guide couldn't be written. Try again.",
    tallerGuidePlaceholder: 'E.g.: open with Ferocious Bellow, not the big attack. Fiery Big Bang 290 + Maximum Belt 50 = 340 against ex.',
    tallerPrizeRuleTitle: 'Mega Evolution ex give 3 Prize cards',
    tallerPrizeRuleBody: 'Half the game in one knockout. A deck running Megas cannot trade blow for blow: your opponent wins on two knockouts and you need three. Either heal it fully or hide it.',
    tallerField: 'What you will run into',
    tallerFieldHelp: 'The field your decks meet. An expected win rate means something different when the field is led by someone you beat.',
    tallerShare: 'Share',
    tallerWinRate: 'Win rate',
    tallerTop8: 'Top 8',
    tallerAnalyse: 'Request analysis',
    tallerReadOnly: 'This board only reads: applying a change is still the builder\'s job.',
    preciosTitle: 'Price watch',
    preciosOpen: 'Open watch',
    preciosSub: 'deals across Chilean stores',
    preciosKpiOpps: 'opportunities · deals in Chilean stores',
    preciosBack: 'Back to panel',
    preciosLastRun: 'Last sweep',
    preciosNoRun: 'No sweep yet: launch it from Data ("Price watch").',
    preciosStoreProducts: '{total} products · {ligadas} offers matched · {disponibles} in stock',
    preciosStoreError: 'last sweep failed',
    preciosOpps: 'Opportunities',
    preciosOppsHelp: 'Prices well below normal, to buy cheap and resell at the usual price. "Normal" is the median of the other stores today, or the last 30 days when nobody else sells it.',
    preciosNoOpps: 'No card is cheap enough right now. The watch keeps looking 4 times a day.',
    preciosCard: 'Card',
    preciosBuyAt: 'Buy at',
    preciosNormally: 'Normal',
    preciosSavings: 'Savings',
    preciosVsHistory: 'vs 30 days',
    preciosStore: 'Store',
    preciosFilterAllStores: 'All stores',
    preciosFilterText: 'Filter by card or set…',
    preciosNoFiltered: 'No opportunity passes these filters.',
    preciosGo: 'Go to store',
    preciosSearch: 'Compare a card by name…',
    preciosSearchHelp: 'Before buying any card: who sells it and for how much.',
    preciosSearchNone: 'No card with a store offer matches that search.',
    preciosOutOfStock: 'out of stock',
    notifPrecio: '"{carta}" at ${precio} on {tienda} (normally ${referencia})',
    navAdmin: 'Admin',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    fbTitle: 'We listen',
    fbIntro: 'PokeTrack improves with what its users tell us: suggestions set the course, and bugs that slip in get fixed sooner when someone reports them.',
    fbNeedsAccount: 'To write to us you need an account with a confirmed email — that way every report comes from a real person.',
    fbTypeLabel: 'What are you telling us',
    fbBug: 'A bug',
    fbSuggestion: 'A suggestion',
    fbPlaceholder: "Tell us what happened or what you'd like to see. Short and direct works great.",
    fbSend: 'Send',
    fbThanks: 'Thanks! We really do read these.',
    fbFailed: "Couldn't send it. Please try again.",
    fbTooMany: "You've sent several today; you can send more tomorrow.",
    fbFootLine: 'We listen to our users:',
    fbFootCta: 'send us a bug or a suggestion',
    fbAdminTitle: 'Suggestion inbox',
    fbAdminNew: 'unread',
    fbAdminDone: 'Handled',
    fbAdminReopen: 'Reopen',
    noDecklistYet: 'This archetype has no saved decklist yet',
    fbAdminBack: 'Back to the panel',
    fbAdminAllRead: 'all handled',
    noArt: 'Illustration not available yet',
    noArtCard: "We don't have this card's illustration yet. It will appear as soon as it's available.",
    cannotUndo: 'This cannot be undone.',
    sheetCraft: 'Craft:',
    sheetCraftValue: '~{n} credits',
    sheetCraftHint: 'Approximate credits to craft this list in Pokémon TCG Live, based on each card’s rarity. Basic energies are free.',
    sheetCraftUnknown: '{n} cards are left out (rarity with no known rate).',
    sheetBuy: 'Buy:',
    sheetBuyHint: 'Sum of the cheapest available single per card on the Chilean market. A reference only: stock and prices change daily.',
    sheetBuyMissing: '{n} cards with no local price are not included.',
    sheetBuyAggregate: '{n} cards are priced from an aggregate (language and condition not verified): the total may fall short.',
    adminDeleteUser: 'Delete account',
    adminDeleteUserTitle: 'Delete the account {correo}?',
    adminDeleteUserDetail: 'Their decks ({mazos}), matches ({partidas}) and subscription are deleted too.',
    cancel: 'Cancel',
    delete: 'Delete',
    confirmDeleteMatch: 'Delete this match?',
    confirmDeleteMatchDetail: 'vs {rival} · {resultado} · {premios}',
    confirmDeleteDeck: 'Delete the deck "{nombre}"?',
    confirmClearDeck: 'Clear the list?',
    confirmClearDeckDetail: 'Removes the {n} cards you have added.',
    clearAction: 'Clear',
    filterOptions: 'Search…',
    noOptions: 'Nothing matches.',
    acceptSuggestionHint: 'Deduced from the Pokemon seen. Click to apply.',
    save: 'Save',
    refreshDenied: 'Your session expired or you are not an admin. Sign in again.',
    needsSession: 'Sign in to save matches.',
    needsSessionMatchesHint: 'Your history, your record per deck and how you do against each archetype live in your account, so you get them on any device.',

    // --- Account ---
    account: 'Account',
    signIn: 'Sign in',
    signUp: 'Create account',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    newPassword: 'New password',
    passwordMin: 'At least 8 characters',
    yourName: 'Your name',
    optional: 'optional',
    inviteCode: 'Invite code',
    forgotPassword: 'Forgot your password?',
    backToSignIn: 'Back to sign in',
    recoverTitle: 'Recover password',
    recoverHint: "We'll email you a link to choose a new password.",
    recoverSent: 'If that email has an account, the link is on its way. Check your spam folder too.',
    resetTitle: 'Choose a new password',
    sending: 'Sending…',
    guestPitch: 'With an account your decks and match history are saved, and they are yours alone.',
    guestBrowsing: 'You can browse cards and metagame without an account.',
    verifyPending: 'Confirm your email to unlock your 3 days of Pro and save matches.',
    verifyResend: 'Resend',
    verifySent: 'Sent. Check your inbox.',
    verifiedOk: 'Done! Your email is confirmed and you get 3 days of Pro: every game one by one, your deck statistics and the analyst.',
    verifiedExpired: 'That link was already used or expired. You can request another from your account.',
    myAccount: 'My account',
    planLabel: 'Plan',
    planFree: 'Free',
    planPro: 'Pro',
    bonusDays: '{n} Pro days from invites',
    myInviteCode: 'Your invite code',
    inviteExplain:
      'When someone you invite saves their first match, you earn Pro days: 7 for the first, 3 for the second, 1 after that.',
    copy: 'Copy',
    errEmailTaken: 'That email already has an account. Try signing in.',
    errInvalidEmail: "That email doesn't look valid.",
    errWeakPassword: 'The password needs at least 8 characters.',
    errBadCredentials: 'Wrong email or password.',
    errInvalidInvite: "That invite code doesn't exist or was already used.",
    errInvalidToken: 'That link was already used or expired. Request another.',
    errTooMany: 'Too many attempts. Wait a bit and try again.',
    errServer: 'The server failed. Try again in a moment.',
    errGeneric: "Couldn't complete. Check the details and try again.",
    substitutedArtHint: 'art from another printing (the list asks for {set} {n})',
    substitutedArtNote:
      '{n} image(s) come from a different printing of the same card. Basic Energy is interchangeable: for play and for buying it makes no difference.',
  },
};

const STORAGE_KEY = 'ptcg-tracker:lang';
const LangContext = createContext(null);

function initialLang() {
  if (typeof window === 'undefined') return 'es';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && STRINGS[saved]) return saved;
  const nav = (window.navigator.language || 'es').slice(0, 2).toLowerCase();
  return STRINGS[nav] ? nav : 'es';
}

/**
 * CIFRAS DE DATOS con separador de miles SIEMPRE, también a cuatro dígitos.
 *
 * `toLocaleString('es')` no agrupa los números de cuatro cifras, y no es un
 * fallo suyo: es la regla del español —la misma por la que un año se escribe
 * 2026 y no 2.026—. El problema es que aquí las cifras no se leen sueltas sino
 * EN FILA, unas al lado de otras, para compararse: en el marcador del meta
 * salía «62 torneos · 6965 jugadores · 17.588 partidas», y ver el punto en una
 * y no en la de al lado se lee como un error de la página, no como ortografía.
 *
 * `useGrouping: 'always'` fuerza la agrupación. Se usa SOLO para cantidades.
 * Nada de años, identificadores ni números de carta: ahí la regla del idioma
 * es la buena y un punto de más los estropearía.
 */
export function cifra(n, lang) {
  return (n ?? 0).toLocaleString(lang, { useGrouping: 'always' });
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(initialLang);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      /**
       * t() cae al espanol si falta una clave, para que nunca se vea el
       * identificador crudo.
       *
       * El segundo argumento sustituye marcadores `{nombre}`. Existe porque hay
       * frases donde el dato va EN MEDIO ("Lista real de slowko"), y partirlas
       * en trozos para concatenar obliga a que el orden de las palabras sea el
       * mismo en los dos idiomas -- que es justo lo que no se puede dar por
       * hecho al traducir.
       */
      t: (key, vars) => {
        const s = STRINGS[lang]?.[key] ?? STRINGS.es[key] ?? key;
        if (!vars) return s;
        return s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
      },
    }),
    [lang]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang debe usarse dentro de <LangProvider>');
  return ctx;
}
